import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ShieldCheck, ShieldX, Clock, Bell, ExternalLink, ArrowLeft, Flag, Trash2, Eye, AlertTriangle, Package, XCircle, Search, ChevronLeft, ChevronRight, ChevronDown, CreditCard, Users, RotateCcw, CheckCircle2, BarChart3, TrendingUp, ShoppingBag, Repeat2, Ban, RotateCw, EyeOff, PackageOpen, Video, Truck, Download, Bug, Copy, Camera, Banknote, Loader2, Heart } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (price) => `Rp ${Number(price).toLocaleString('id-ID')}`;

/**
 * OrderItemsBlock — renders the product/brand/photo block on an admin order
 * card. For single-item orders it behaves exactly like the legacy single-photo
 * layout. For multi-item orders (single-seller, multi-listing checkout) it
 * shows stacked thumbnails + "N items ▾" toggle that expands to a per-item
 * breakdown (photo, brand, name, qty × price). Used across Pending Payment,
 * Refund Buyer, Cancellations, Pay Seller, and Transactions tabs so admins
 * can see every item that a single order ID covers.
 */
function OrderItemsBlock({ items, fallbackPhoto, fallbackBrand, fallbackProductName, orderId, compact = false, dense = false }) {
  const [expanded, setExpanded] = React.useState(false);
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const isMulti = list.length > 1;

  if (!isMulti) {
    const single = list[0] || {};
    const photo = single.photo || fallbackPhoto;
    const brand = single.brand || fallbackBrand;
    const productName = single.product_name || fallbackProductName || 'Order';
    const size = compact ? 'w-12 h-12' : 'w-14 h-14';
    const iconSize = compact ? 18 : 20;
    return (
      <div className="flex items-center gap-4 min-w-0">
        {photo ? (
          <img src={photo} alt="" className={`${size} rounded-xl object-cover flex-shrink-0 border border-cream-100`} />
        ) : (
          <div className={`${size} rounded-xl bg-cream-100 flex items-center justify-center flex-shrink-0`}>
            <Package size={iconSize} className="text-cream-300" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-xs font-body text-[#A89F91] font-mono">#{orderId}</p>
          {brand && <p className="text-[10px] font-body uppercase tracking-wider text-[#A89F91]">{brand}</p>}
          <p className={`font-body font-semibold text-[#2C2A29] text-sm ${compact ? 'truncate' : ''}`}>{productName}</p>
        </div>
      </div>
    );
  }

  // Multi-item: stacked thumbnails + expandable list
  const previewCount = Math.min(3, list.length);
  const moreCount = list.length - previewCount;
  return (
    <div className="min-w-0 flex-1">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
        className="flex items-center gap-3 min-w-0 w-full text-left group"
        data-testid={`order-items-toggle-${orderId}`}
        aria-expanded={expanded}
      >
        <div className="flex -space-x-3 flex-shrink-0">
          {list.slice(0, previewCount).map((it, idx) => (
            it.photo ? (
              <img
                key={idx}
                src={it.photo}
                alt=""
                className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm"
                style={{ zIndex: previewCount - idx }}
              />
            ) : (
              <div
                key={idx}
                className="w-12 h-12 rounded-xl bg-cream-100 flex items-center justify-center border-2 border-white shadow-sm"
                style={{ zIndex: previewCount - idx }}
              >
                <Package size={16} className="text-cream-300" />
              </div>
            )
          ))}
          {moreCount > 0 && (
            <div className="w-12 h-12 rounded-xl bg-[#2C2A29] text-white text-xs font-body font-semibold flex items-center justify-center border-2 border-white shadow-sm" style={{ zIndex: 0 }}>
              +{moreCount}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-body text-[#A89F91] font-mono">#{orderId}</p>
          <p className="font-body font-semibold text-[#2C2A29] text-sm flex items-center gap-1.5">
            {list.length} items in this order
            <ChevronDown size={14} className={`text-[#78716C] transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </p>
          <p className="text-[11px] font-body text-[#A89F91] truncate">
            {list.map(it => `${it.brand ? it.brand + ' ' : ''}${it.product_name || ''}`).filter(Boolean).join(' · ')}
          </p>
        </div>
      </button>
      {expanded && (
        <div className={`mt-3 space-y-2 bg-cream-50 rounded-xl ${dense ? 'p-2' : 'p-3'}`} data-testid={`order-items-list-${orderId}`}>
          {list.map((it, idx) => {
            const lineTotal = (Number(it.item_price || 0) * Number(it.quantity || 1));
            return (
              <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-2 border border-cream-100/70">
                {it.photo ? (
                  <img src={it.photo} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-cream-100" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-cream-100 flex items-center justify-center flex-shrink-0">
                    <Package size={14} className="text-cream-300" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {it.brand && <p className="text-[10px] font-body uppercase tracking-wider text-[#A89F91]">{it.brand}</p>}
                  <p className="font-body text-sm text-[#2C2A29] truncate">{it.product_name || 'Item'}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="font-body text-xs text-[#78716C]">
                    {it.quantity || 1} × {formatPrice(it.item_price || 0)}
                  </p>
                  <p className="font-body text-sm font-semibold text-[#C5A880]">{formatPrice(lineTotal)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState({ open: false, userId: null, name: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectOtherText, setRejectOtherText] = useState('');
  const [processing, setProcessing] = useState(null);
  const validTabs = ['metrics', 'analytics', 'verifications', 'listings', 'transactions', 'users', 'reports', 'flags', 'orders', 'refunds', 'cancellations'];
  const hashTab = location.hash?.replace('#', '');
  const [activeTab, setActiveTabState] = useState(validTabs.includes(hashTab) ? hashTab : 'verifications');
  const setActiveTab = (tab) => { setActiveTabState(tab); window.history.replaceState(null, '', `#${tab}`); };
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [flags, setFlags] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [cancelDialog, setCancelDialog] = useState({ open: false, orderId: null, productName: '' });
  const [allListings, setAllListings] = useState([]);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [listingsPage, setListingsPage] = useState(1);
  const [listingsSearch, setListingsSearch] = useState('');
  const [listingsStatusFilter, setListingsStatusFilter] = useState('active');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, listingId: null, productName: '' });
  // Admin video moderation — mirrors the listings tab. Same status filter
  // (active / removed / all) and same delete-confirmation pattern.
  const [allVideos, setAllVideos] = useState([]);
  const [videosTotal, setVideosTotal] = useState(0);
  const [videosPage, setVideosPage] = useState(1);
  const [videosSearch, setVideosSearch] = useState('');
  const [videosStatusFilter, setVideosStatusFilter] = useState('active');
  const [videoDeleteDialog, setVideoDeleteDialog] = useState({ open: false, videoId: null, title: '' });
  const [allOrders, setAllOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersSearch, setOrdersSearch] = useState('');
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('');
  const [deleteOrderDialog, setDeleteOrderDialog] = useState({ open: false, orderId: null, orderId_display: '' });
  const [markPaidDialog, setMarkPaidDialog] = useState({ open: false, orderId: null, amount: 0, note: '' });
  const [adminUsers, setAdminUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, userId: null, name: '', email: '' });
  // Bank-info dialog — admin clicks the banknote icon on a user row to inspect
  // their saved payout bank without leaving the Users tab.
  const [bankDialog, setBankDialog] = useState({ open: false, loading: false, user: null, bank: null });

  const openBankDialog = async (u) => {
    setBankDialog({ open: true, loading: true, user: u, bank: null });
    try {
      const { data } = await axios.get(`${API}/admin/users/${u.user_id}/payout-bank`);
      setBankDialog({ open: true, loading: false, user: u, bank: data.payout_bank || null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load bank info');
      setBankDialog({ open: false, loading: false, user: null, bank: null });
    }
  };
  const [refundRequests, setRefundRequests] = useState([]);
  const [cancelRequests, setCancelRequests] = useState([]);
  const [returnRequests, setReturnRequests] = useState([]);
  const [returnDecisionDialog, setReturnDecisionDialog] = useState({ open: false, ret: null, action: '' });
  const [komerceDebug, setKomerceDebug] = useState({ open: false, orderId: null, data: null });
  const [returnAdminNote, setReturnAdminNote] = useState('');
  const [payoutOrders, setPayoutOrders] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsPeriod, setMetricsPeriod] = useState('all');

  // ── Analytics tab (downloadable reports) ──
  const todayStr = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgoStr = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [analyticsFrom, setAnalyticsFrom] = useState(thirtyDaysAgoStr);
  const [analyticsTo, setAnalyticsTo] = useState(todayStr);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [salesSummary, setSalesSummary] = useState(null);
  const [salesByDay, setSalesByDay] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [returnsAnalysis, setReturnsAnalysis] = useState(null);
  const [pendingPayoutsReport, setPendingPayoutsReport] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    if (!user?.is_admin) return;
    setAnalyticsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('session_token')}` };
      const params = { from: analyticsFrom, to: analyticsTo };
      const [s1, s2, s3, s4, s5] = await Promise.all([
        axios.get(`${API}/admin/analytics/sales-summary`, { headers, params }),
        axios.get(`${API}/admin/analytics/sales-by-day`, { headers, params }),
        axios.get(`${API}/admin/analytics/top-sellers`, { headers, params: { ...params, limit: 20 } }),
        axios.get(`${API}/admin/analytics/returns-analysis`, { headers, params }),
        axios.get(`${API}/admin/analytics/pending-payouts`, { headers }),
      ]);
      setSalesSummary(s1.data);
      setSalesByDay(s2.data.series || []);
      setTopSellers(s3.data.top_sellers || []);
      setReturnsAnalysis(s4.data);
      setPendingPayoutsReport(s5.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [user, analyticsFrom, analyticsTo]);

  const downloadCsv = async (reportName, filenameBase) => {
    try {
      const token = localStorage.getItem('session_token');
      const isPayouts = reportName === 'pending-payouts';
      const url = isPayouts
        ? `${API}/admin/analytics/${reportName}?format=csv`
        : `${API}/admin/analytics/${reportName}?format=csv&from=${analyticsFrom}&to=${analyticsTo}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const dateTag = isPayouts ? todayStr : `${analyticsFrom}_${analyticsTo}`;
      link.download = `${filenameBase}_${dateTag}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error('Failed to download CSV');
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_admin) { navigate('/'); return; }
    fetchPending(true);
    fetchReports();
    fetchFlags();
    fetchPendingOrders();
    fetchRefundRequests();
    fetchCancelRequests();
    fetchReturnRequests();
    fetchPayoutOrders();
  }, [user, navigate, authLoading]);

  // Auto-poll admin data every 20s + instant refetch on tab visible / window focus
  useEffect(() => {
    if (authLoading || !user?.is_admin) return;
    const poll = () => {
      fetchPending();
      fetchPendingOrders();
      fetchRefundRequests();
      fetchCancelRequests();
      fetchReports();
      fetchFlags();
      fetchReturnRequests();
      fetchPayoutOrders();
    };
    const interval = setInterval(poll, 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    const onFocus = () => poll();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [authLoading, user]);

  // Auto-poll tab-specific data every 20s + visibility/focus refetch
  useEffect(() => {
    if (authLoading || !user?.is_admin) return;
    let poll = null;
    if (activeTab === 'listings') poll = () => fetchListings();
    else if (activeTab === 'videos') poll = () => fetchAdminVideos();
    else if (activeTab === 'transactions') poll = () => fetchAllOrders();
    else if (activeTab === 'users') poll = () => fetchAdminUsers();
    if (!poll) return;
    const interval = setInterval(poll, 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    const onFocus = () => poll();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [authLoading, user, activeTab]);

  useEffect(() => {
    if (activeTab === 'listings') fetchListings();
  }, [activeTab, listingsPage, listingsSearch, listingsStatusFilter]);

  useEffect(() => {
    if (activeTab === 'videos') fetchAdminVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, videosPage, videosSearch, videosStatusFilter]);

  useEffect(() => {
    if (activeTab === 'transactions') fetchAllOrders();
  }, [activeTab, ordersPage, ordersSearch, ordersStatusFilter]);

  useEffect(() => {
    if (activeTab === 'users') fetchAdminUsers();
  }, [activeTab, usersPage, usersSearch]);

  useEffect(() => {
    if (activeTab === 'metrics') fetchMetrics(metricsPeriod);
  }, [activeTab, metricsPeriod]);

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab, fetchAnalytics]);

  const fetchFlags = async () => {
    try {
      const res = await axios.get(`${API}/admin/flags`);
      setFlags(res.data);
    } catch { /* ignore */ }
  };

  const fetchMetrics = useCallback(async (period) => {
    setMetricsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/metrics?period=${period || metricsPeriod}`);
      setMetrics(res.data);
    } catch { /* ignore */ }
    finally { setMetricsLoading(false); }
  }, [metricsPeriod]);

  const handleFlagAction = async (flag, action) => {
    const flagId = typeof flag === 'string' ? flag : flag.flag_id;
    setProcessing(flagId);
    try {
      const payload = { action };
      if (typeof flag === 'object' && flag.type === 'duplicate_abuse') {
        payload.user_id = flag.user_id;
        payload.source_listing_id = flag.source_listing_id;
      }
      await axios.post(`${API}/admin/flags/${flagId}`, payload);
      toast.success(action === 'block' ? 'User blocked & listings deactivated' : 'Flag dismissed');
      setFlags(f => f.filter(fl => fl.flag_id !== flagId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  const fetchPendingOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/pending`);
      setPendingOrders(res.data);
    } catch { /* ignore */ }
  };

  const handleCancelOrder = async (orderId) => {
    setProcessing(orderId);
    try {
      await axios.post(`${API}/admin/orders/${orderId}/cancel`, {});
      toast.success('Order cancelled, stock restored');
      setPendingOrders(prev => prev.filter(o => o.order_id !== orderId));
      setCancelDialog({ open: false, orderId: null, productName: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setProcessing(null);
    }
  };

  const fetchListings = async () => {
    try {
      const res = await axios.get(`${API}/admin/listings`, {
        params: { q: listingsSearch, page: listingsPage, status: listingsStatusFilter },
      });
      setAllListings(res.data.listings);
      setListingsTotal(res.data.total);
    } catch { /* ignore */ }
  };

  const handleRemoveListing = async (listingId) => {
    setProcessing(listingId);
    try {
      await axios.post(`${API}/admin/listings/${listingId}/remove`);
      toast.success('Listing removed from marketplace');
      fetchListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setProcessing(null);
    }
  };

  const handleRestoreListing = async (listingId) => {
    setProcessing(listingId);
    try {
      await axios.post(`${API}/admin/listings/${listingId}/restore`);
      toast.success('Listing restored to marketplace');
      fetchListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteListing = async (listingId) => {
    setProcessing(listingId);
    try {
      await axios.delete(`${API}/admin/listings/${listingId}`);
      toast.success('Listing permanently deleted');
      setAllListings(prev => prev.filter(l => l.listing_id !== listingId));
      setListingsTotal(prev => prev - 1);
      setDeleteDialog({ open: false, listingId: null, productName: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessing(null);
    }
  };

  const fetchAllOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/all`, {
        params: { q: ordersSearch, status: ordersStatusFilter, page: ordersPage },
      });
      setAllOrders(res.data.orders);
      setOrdersTotal(res.data.total);
    } catch { /* ignore */ }
  };

  // ── Admin video moderation helpers (mirror the listings ones) ──
  const fetchAdminVideos = async () => {
    try {
      const res = await axios.get(`${API}/admin/videos`, {
        params: { q: videosSearch, page: videosPage, status: videosStatusFilter },
      });
      setAllVideos(res.data.videos);
      setVideosTotal(res.data.total);
    } catch { /* ignore */ }
  };

  const handleRemoveVideo = async (videoId) => {
    setProcessing(videoId);
    try {
      await axios.post(`${API}/admin/videos/${videoId}/remove`);
      toast.success('Video taken down');
      fetchAdminVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove video');
    } finally {
      setProcessing(null);
    }
  };

  const handleRestoreVideo = async (videoId) => {
    setProcessing(videoId);
    try {
      await axios.post(`${API}/admin/videos/${videoId}/restore`);
      toast.success('Video restored');
      fetchAdminVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore video');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    setProcessing(videoId);
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`);
      toast.success('Video permanently deleted');
      setAllVideos(prev => prev.filter(v => v.video_id !== videoId));
      setVideosTotal(prev => prev - 1);
      setVideoDeleteDialog({ open: false, videoId: null, title: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete video');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    setProcessing(orderId);
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      toast.success('Order deleted');
      setAllOrders(prev => prev.filter(o => o.order_id !== orderId));
      setOrdersTotal(prev => prev - 1);
      setDeleteOrderDialog({ open: false, orderId: null, orderId_display: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkPaidManual = async () => {
    const { orderId, note } = markPaidDialog;
    if (!orderId) return;
    if (!note.trim()) { toast.error('Please add a reconciliation note (e.g., transaction reference)'); return; }
    setProcessing(orderId);
    try {
      await axios.post(`${API}/admin/orders/${orderId}/mark-paid-manual`, { admin_note: note.trim() });
      toast.success(`Order #${orderId} reconciled as paid`);
      setAllOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, payment_status: 'paid', payment_method: 'manual_reconcile' } : o));
      setMarkPaidDialog({ open: false, orderId: null, amount: 0, note: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile');
    } finally {
      setProcessing(null);
    }
  };

  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports`);
      setReports(res.data);
    } catch { /* ignore */ }
    finally { setReportsLoading(false); }
  };

  const fetchPending = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pending-verifications`);
      setPending(res.data);
    } catch {
      if (showLoading) toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, name) => {
    setProcessing(userId);
    try {
      await axios.post(`${API}/admin/verify/${userId}`, { action: 'approve' });
      toast.success(`${name} approved as influencer!`);
      setPending(p => p.filter(v => v.user_id !== userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    setProcessing(rejectDialog.userId);
    try {
      const reason = rejectReason === 'other' ? `other:${rejectOtherText.trim()}` : rejectReason;
      await axios.post(`${API}/admin/verify/${rejectDialog.userId}`, { action: 'reject', reason });
      toast.success(`${rejectDialog.name} rejected`);
      setPending(p => p.filter(v => v.user_id !== rejectDialog.userId));
      setRejectDialog({ open: false, userId: null, name: '' });
      setRejectReason('');
      setRejectOtherText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const handleReportAction = async (reportId, action) => {
    setProcessing(reportId);
    try {
      await axios.post(`${API}/admin/reports/${reportId}`, { action });
      toast.success(`Report ${action}d`);
      setReports(r => r.filter(rep => rep.report_id !== reportId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const platformUrl = (platform, handle) => {
    const clean = handle?.replace('@', '') || '';
    if (platform === 'Instagram') return `https://instagram.com/${clean}`;
    if (platform === 'TikTok') return `https://tiktok.com/@${clean}`;
    if (platform === 'YouTube') return `https://youtube.com/@${clean}`;
    if (platform === 'Facebook') return `https://facebook.com/${clean}`;
    if (platform === 'Twitter') return `https://twitter.com/${clean}`;
    return '#';
  };

  const fetchAdminUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { q: usersSearch, page: usersPage }
      });
      setAdminUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch { /* ignore */ }
  };

  const handleDeactivateUser = async (userId) => {
    setProcessing(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/deactivate`);
      toast.success('User deactivated');
      fetchAdminUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    } finally {
      setProcessing(null);
    }
  };

  const handleReactivateUser = async (userId) => {
    setProcessing(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/reactivate`);
      toast.success('User reactivated');
      fetchAdminUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setProcessing(userId);
    try {
      const res = await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(res.data.message);
      setDeleteUserDialog({ open: false, userId: null, name: '', email: '' });
      fetchAdminUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setProcessing(null);
    }
  };

  const fetchRefundRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/refund-requests`);
      setRefundRequests(res.data);
    } catch (err) { console.warn('[Admin] fetchRefundRequests failed:', err?.response?.status, err?.response?.data || err?.message); }
  };

  // Deprecated: legacy refund approve/dismiss endpoints were removed.
  // The Refunds tab now renders orders with refund_required=true and uses
  // the Mark Refunded button wired to /admin/orders/{id}/mark-refunded inline.

  const fetchCancelRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/cancel-requests`);
      setCancelRequests(res.data);
    } catch (err) { console.warn('[Admin] fetchCancelRequests failed:', err?.response?.status, err?.response?.data || err?.message); }
  };

  const handleCancelAction = async (cancelId, action) => {
    setProcessing(cancelId);
    try {
      await axios.post(`${API}/admin/cancel-requests/${cancelId}/${action}`);
      toast.success(action === 'approve' ? 'Cancellation approved. Order cancelled and stock restored.' : 'Cancellation dismissed. Seller must proceed with shipping.');
      setCancelRequests(prev => prev.filter(r => r.cancel_id !== cancelId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  const fetchReturnRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/returns`);
      setReturnRequests(res.data);
    } catch (err) { console.warn('[Admin] fetchReturnRequests failed:', err?.response?.status, err?.response?.data || err?.message); }
  };

  const fetchPayoutOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/payouts`);
      setPayoutOrders(res.data);
    } catch (err) { console.warn('[Admin] fetchPayoutOrders failed:', err?.response?.status, err?.response?.data || err?.message); }
  };

  const handleReturnAction = async (returnId, action, returnType) => {
    setProcessing(returnId);
    try {
      if (action === 'reject') {
        await axios.post(`${API}/admin/returns/${returnId}/reject`, { admin_note: returnAdminNote });
        toast.success('Return request rejected');
      } else if (action === 'approve') {
        await axios.post(`${API}/admin/returns/${returnId}/approve`, { return_type: returnType, admin_note: returnAdminNote });
        toast.success(`Return approved as ${returnType === 'refund_only' ? 'Refund Only' : 'Return Required'}`);
      } else if (action === 'process-refund') {
        await axios.post(`${API}/admin/returns/${returnId}/process-refund`, { admin_note: returnAdminNote });
        toast.success('Refund processed — buyer will be refunded (item + shipping)');
      } else if (action === 'dispute_accept') {
        await axios.post(`${API}/admin/returns/${returnId}/resolve-dispute`, { decision: 'accept', admin_note: returnAdminNote });
        toast.success('Dispute accepted — order moved to Pay Seller queue');
        // Refresh payout list so the new entry appears immediately.
        try { const res = await axios.get(`${API}/admin/payouts`); setPayoutOrders(res.data); } catch (_) { /* non-fatal */ }
      } else if (action === 'dispute_reject') {
        await axios.post(`${API}/admin/returns/${returnId}/resolve-dispute`, { decision: 'reject', admin_note: returnAdminNote });
        toast.success('Dispute rejected — order moved to Refund Buyer queue');
        try { const res = await axios.get(`${API}/admin/refunds`); setRefundRequests(res.data); } catch (_) { /* non-fatal */ }
      } else if (action === 'pay_seller') {
        await axios.post(`${API}/admin/returns/${returnId}/pay-seller`, { admin_note: returnAdminNote });
        toast.success('Seller payout recorded (item price only; shipping retained)');
      }
      setReturnDecisionDialog({ open: false, ret: null, action: '' });
      setReturnAdminNote('');
      fetchReturnRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  if (authLoading || !user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-body text-sm text-[#78716C] hover:text-[#2C2A29] mb-6 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mb-2" data-testid="admin-title">Admin Panel</h1>
          <p className="font-body text-[#78716C]">Manage verifications, orders, listings, and reports</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-cream-50 rounded-full p-1 w-fit flex-wrap" data-testid="admin-tabs">
          <button onClick={() => setActiveTab('metrics')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'metrics' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-metrics">
            <BarChart3 size={14} /> Metrics
          </button>
          <button onClick={() => setActiveTab('analytics')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'analytics' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-analytics">
            <TrendingUp size={14} /> Analytics
          </button>
          <button onClick={() => setActiveTab('verifications')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'verifications' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-verifications">
            <ShieldCheck size={14} /> Verifications {pending.length > 0 && `(${pending.length})`}
          </button>
          <button onClick={() => setActiveTab('orders')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'orders' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-orders">
            <Package size={14} /> Pending Payment {pendingOrders.length > 0 && `(${pendingOrders.length})`}
          </button>
          <button onClick={() => setActiveTab('refunds')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'refunds' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-refunds">
            <RotateCcw size={14} /> Refund Buyer {refundRequests.length > 0 && `(${refundRequests.length})`}
          </button>
          <button onClick={() => setActiveTab('returns')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'returns' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-returns">
            <PackageOpen size={14} /> Return/Dispute {returnRequests.filter(r => ['pending', 'return_received'].includes(r.status) || (r.seller_dispute && !r.dispute_resolution)).length > 0 && `(${returnRequests.filter(r => ['pending', 'return_received'].includes(r.status) || (r.seller_dispute && !r.dispute_resolution)).length})`}
          </button>
          <button onClick={() => setActiveTab('cancellations')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'cancellations' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-cancellations">
            <XCircle size={14} /> Cancellations {cancelRequests.length > 0 && `(${cancelRequests.length})`}
          </button>
          <button onClick={() => setActiveTab('payouts')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'payouts' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-payouts">
            <CreditCard size={14} /> Pay Seller {payoutOrders.length > 0 && `(${payoutOrders.length})`}
          </button>
          <button onClick={() => setActiveTab('listings')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'listings' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-listings">
            <Eye size={14} /> Listings
          </button>
          <button onClick={() => setActiveTab('videos')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'videos' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-videos">
            <Video size={14} /> Videos
          </button>
          <button onClick={() => setActiveTab('transactions')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'transactions' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-transactions">
            <CreditCard size={14} /> Transactions {(() => {
              const inFlight = allOrders.filter(o => o.payment_status === 'paid' || o.payment_status === 'shipped').length;
              return inFlight > 0 ? `(${inFlight})` : '';
            })()}
          </button>
          <button onClick={() => setActiveTab('reports')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'reports' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-reports">
            <Flag size={14} /> Reports {reports.length > 0 && `(${reports.length})`}
          </button>
          <button onClick={() => setActiveTab('flags')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'flags' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-flags">
            <AlertTriangle size={14} /> Flags {flags.length > 0 && `(${flags.length})`}
          </button>
          <button onClick={() => setActiveTab('users')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all flex items-center gap-1.5 ${
              activeTab === 'users' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-users">
            <Users size={14} /> Users
          </button>
        </div>

        {activeTab === 'metrics' && (
          <div data-testid="metrics-dashboard">
            {/* Period Filter */}
            <div className="flex gap-2 mb-6" data-testid="metrics-period-filter">
              {[
                { value: 'all', label: 'All Time' },
                { value: '7d', label: '7 Days' },
                { value: '30d', label: '30 Days' },
                { value: 'month', label: 'This Month' },
              ].map(p => (
                <button key={p.value} onClick={() => setMetricsPeriod(p.value)}
                  className={`rounded-full px-4 py-1.5 text-xs font-body font-medium transition-all ${
                    metricsPeriod === p.value ? 'bg-[#2C2A29] text-white' : 'bg-white text-[#78716C] border border-cream-100 hover:border-[#2C2A29]'
                  }`} data-testid={`metrics-period-${p.value}`}>{p.label}</button>
              ))}
            </div>

            {metricsLoading && !metrics ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[1,2,3,4,5,6].map(i => <div key={i} className="h-28 bg-white rounded-2xl animate-pulse border border-cream-100/50" />)}
              </div>
            ) : metrics ? (
              <>
                {/* Main KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-2xl p-5 border border-cream-100/50" data-testid="metric-total-listings">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-[#FFF5F5] rounded-lg flex items-center justify-center"><ShoppingBag size={16} className="text-[#D48585]" /></div>
                      <span className="text-xs font-body text-[#A89F91] uppercase tracking-wider">Total Listings</span>
                    </div>
                    <p className="font-heading text-2xl text-[#2C2A29]">{metrics.total_listings}</p>
                    <p className="text-xs font-body text-[#78716C] mt-1">{metrics.active_listings} active</p>
                  </div>

                  <div className="bg-white rounded-2xl p-5 border border-cream-100/50" data-testid="metric-transactions">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center"><CheckCircle2 size={16} className="text-emerald-600" /></div>
                      <span className="text-xs font-body text-[#A89F91] uppercase tracking-wider">Transactions</span>
                    </div>
                    <p className="font-heading text-2xl text-[#2C2A29]">{metrics.total_transactions}</p>
                    <p className="text-xs font-body text-[#78716C] mt-1">completed sales</p>
                  </div>

                  <div className="bg-white rounded-2xl p-5 border border-cream-100/50 col-span-2 md:col-span-1" data-testid="metric-gmv">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center"><TrendingUp size={16} className="text-blue-600" /></div>
                      <span className="text-xs font-body text-[#A89F91] uppercase tracking-wider">GMV</span>
                    </div>
                    <p className="font-heading text-2xl text-[#2C2A29]">{formatPrice(metrics.gmv)}</p>
                    <p className="text-xs font-body text-[#78716C] mt-1">gross merchandise value</p>
                  </div>
                </div>

                {/* Repeat Usage */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-2xl p-5 border border-cream-100/50" data-testid="metric-repeat-buyers">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center"><Repeat2 size={16} className="text-purple-600" /></div>
                      <span className="text-xs font-body text-[#A89F91] uppercase tracking-wider">Repeat Buyers</span>
                    </div>
                    <p className="font-heading text-2xl text-[#2C2A29]">{metrics.repeat_buyers}</p>
                    <p className="text-xs font-body text-[#78716C] mt-1">of {metrics.total_buyers} total buyer{metrics.total_buyers !== 1 ? 's' : ''}</p>
                  </div>

                  <div className="bg-white rounded-2xl p-5 border border-cream-100/50" data-testid="metric-repeat-sellers">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center"><Repeat2 size={16} className="text-amber-600" /></div>
                      <span className="text-xs font-body text-[#A89F91] uppercase tracking-wider">Repeat Sellers</span>
                    </div>
                    <p className="font-heading text-2xl text-[#2C2A29]">{metrics.repeat_sellers}</p>
                    <p className="text-xs font-body text-[#78716C] mt-1">of {metrics.total_sellers} total seller{metrics.total_sellers !== 1 ? 's' : ''}</p>
                  </div>
                </div>

                {/* Users */}
                <div className="bg-white rounded-2xl p-5 border border-cream-100/50 mb-6 w-fit" data-testid="metric-total-users">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-cream-50 rounded-lg flex items-center justify-center"><Users size={16} className="text-[#78716C]" /></div>
                    <span className="text-xs font-body text-[#A89F91] uppercase tracking-wider">Registered Users</span>
                  </div>
                  <p className="font-heading text-2xl text-[#2C2A29]">{metrics.total_users}</p>
                  <p className="text-xs font-body text-[#78716C] mt-1">excluding admins</p>
                </div>

                {/* Trend Chart */}
                {metrics.trends && metrics.trends.length > 0 && (
                  <div className="bg-white rounded-2xl p-5 border border-cream-100/50" data-testid="metrics-trends">
                    <h3 className="font-heading text-base text-[#2C2A29] mb-4">Daily Transactions (Last 14 Days)</h3>
                    <div className="flex items-end gap-1.5 h-32">
                      {(() => {
                        const maxTx = Math.max(...metrics.trends.map(t => t.transactions), 1);
                        return metrics.trends.map((t, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                            <div className="absolute bottom-full mb-1 bg-[#2C2A29] text-white text-[10px] font-body rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              {t.transactions} sale{t.transactions !== 1 ? 's' : ''} &middot; {formatPrice(t.gmv)}
                            </div>
                            <div
                              className="w-full bg-[#F4C2C2] rounded-t-md transition-all hover:bg-[#D48585] min-h-[2px]"
                              style={{ height: `${Math.max((t.transactions / maxTx) * 100, 2)}%` }}
                            />
                            <span className="text-[9px] font-body text-[#A89F91] -rotate-45 origin-top-left whitespace-nowrap">{t.date.split(' ')[1]}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                <p className="text-xs font-body text-[#A89F91] mt-6">
                  Definitions: Transactions = completed sales (incl. auto-completed). GMV = total order value before fees. Repeat = more than 1 completed transaction. Demo listings excluded.
                </p>
              </>
            ) : (
              <p className="text-sm font-body text-[#78716C]">Failed to load metrics.</p>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6" data-testid="analytics-tab">
            {/* Date range controls */}
            <div className="bg-white rounded-2xl p-5 border border-cream-100/50">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-xs font-body text-[#78716C] mb-1">From</label>
                  <input
                    type="date"
                    value={analyticsFrom}
                    onChange={e => setAnalyticsFrom(e.target.value)}
                    max={analyticsTo}
                    className="border border-cream-200 rounded-lg px-3 py-2 text-sm font-body bg-white"
                    data-testid="analytics-from-date"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body text-[#78716C] mb-1">To</label>
                  <input
                    type="date"
                    value={analyticsTo}
                    onChange={e => setAnalyticsTo(e.target.value)}
                    min={analyticsFrom}
                    max={todayStr}
                    className="border border-cream-200 rounded-lg px-3 py-2 text-sm font-body bg-white"
                    data-testid="analytics-to-date"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Last 7 days', days: 7 },
                    { label: 'Last 30 days', days: 30 },
                    { label: 'Last 90 days', days: 90 },
                  ].map(p => (
                    <button
                      key={p.label}
                      onClick={() => {
                        setAnalyticsFrom(new Date(Date.now() - p.days * 86400000).toISOString().slice(0, 10));
                        setAnalyticsTo(todayStr);
                      }}
                      className="text-xs font-body text-[#9CAF88] border border-[#9CAF88]/40 hover:bg-[#9CAF88]/10 rounded-full px-3 py-1.5"
                      data-testid={`analytics-quick-${p.days}d`}
                    >{p.label}</button>
                  ))}
                  <button
                    onClick={() => {
                      const now = new Date();
                      setAnalyticsFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
                      setAnalyticsTo(todayStr);
                    }}
                    className="text-xs font-body text-[#9CAF88] border border-[#9CAF88]/40 hover:bg-[#9CAF88]/10 rounded-full px-3 py-1.5"
                    data-testid="analytics-quick-month"
                  >This month</button>
                </div>
                <button
                  onClick={fetchAnalytics}
                  disabled={analyticsLoading}
                  className="ml-auto bg-[#2C2A29] text-white rounded-full px-5 py-2 text-sm font-body font-medium hover:bg-[#1a1818] disabled:opacity-50"
                  data-testid="analytics-refresh-btn"
                >{analyticsLoading ? 'Loading…' : 'Refresh'}</button>
              </div>
            </div>

            {/* 1. Sales Summary */}
            <div className="bg-white rounded-2xl p-6 border border-cream-100/50" data-testid="report-sales-summary">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl text-[#2C2A29]">Sales Summary</h3>
                  <p className="text-xs font-body text-[#78716C] mt-1">Top-line health: GMV, refunds, net revenue, AOV.</p>
                </div>
                <button
                  onClick={() => downloadCsv('sales-summary', 'sales-summary')}
                  className="text-xs font-body text-[#9CAF88] border border-[#9CAF88]/40 hover:bg-[#9CAF88]/10 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                  data-testid="download-sales-summary-csv"
                ><Download size={12} /> Download CSV</button>
              </div>
              {salesSummary ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'GMV', value: formatPrice(salesSummary.gmv) },
                    { label: 'Refunded', value: formatPrice(salesSummary.refunded_amount), tone: 'rose' },
                    { label: 'Net Revenue', value: formatPrice(salesSummary.net_revenue), tone: 'emerald' },
                    { label: 'Orders', value: salesSummary.order_count },
                    { label: 'Avg Order Value', value: formatPrice(salesSummary.avg_order_value) },
                    { label: 'Items Sold', value: salesSummary.items_sold },
                  ].map(m => (
                    <div key={m.label} className="bg-cream-50 rounded-xl p-3">
                      <p className="text-xs font-body text-[#78716C]">{m.label}</p>
                      <p className={`text-lg font-heading ${m.tone === 'emerald' ? 'text-emerald-600' : m.tone === 'rose' ? 'text-rose-600' : 'text-[#2C2A29]'}`}>{m.value}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm font-body text-[#78716C]">No data.</p>}
            </div>

            {/* 2. Sales by Day */}
            <div className="bg-white rounded-2xl p-6 border border-cream-100/50" data-testid="report-sales-by-day">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl text-[#2C2A29]">Sales by Day</h3>
                  <p className="text-xs font-body text-[#78716C] mt-1">Daily breakdown — useful for spotting weekend/weekday patterns.</p>
                </div>
                <button
                  onClick={() => downloadCsv('sales-by-day', 'sales-by-day')}
                  className="text-xs font-body text-[#9CAF88] border border-[#9CAF88]/40 hover:bg-[#9CAF88]/10 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                  data-testid="download-sales-by-day-csv"
                ><Download size={12} /> Download CSV</button>
              </div>
              {salesByDay.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-body">
                    <thead><tr className="text-left text-[#78716C] border-b border-cream-100">
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Orders</th>
                      <th className="py-2">GMV</th>
                    </tr></thead>
                    <tbody>{salesByDay.map(r => (
                      <tr key={r.date} className="border-b border-cream-50">
                        <td className="py-2 pr-4 text-[#2C2A29]">{r.date}</td>
                        <td className="py-2 pr-4 text-[#2C2A29]">{r.orders}</td>
                        <td className="py-2 text-[#2C2A29]">{formatPrice(r.gmv)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <p className="text-sm font-body text-[#78716C]">No sales in this date range.</p>}
            </div>

            {/* 3. Top Sellers */}
            <div className="bg-white rounded-2xl p-6 border border-cream-100/50" data-testid="report-top-sellers">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl text-[#2C2A29]">Top Sellers</h3>
                  <p className="text-xs font-body text-[#78716C] mt-1">Top 20 sellers by GMV in this date range.</p>
                </div>
                <button
                  onClick={() => downloadCsv('top-sellers', 'top-sellers')}
                  className="text-xs font-body text-[#9CAF88] border border-[#9CAF88]/40 hover:bg-[#9CAF88]/10 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                  data-testid="download-top-sellers-csv"
                ><Download size={12} /> Download CSV</button>
              </div>
              {topSellers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-body">
                    <thead><tr className="text-left text-[#78716C] border-b border-cream-100">
                      <th className="py-2 pr-4">#</th>
                      <th className="py-2 pr-4">Seller</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Orders</th>
                      <th className="py-2">GMV</th>
                    </tr></thead>
                    <tbody>{topSellers.map((s, i) => (
                      <tr key={s.seller_id} className="border-b border-cream-50">
                        <td className="py-2 pr-4 text-[#78716C]">{i + 1}</td>
                        <td className="py-2 pr-4 text-[#2C2A29]">
                          {s.name}
                          {s.is_influencer && <span className="ml-2 text-[10px] bg-purple-50 text-purple-700 rounded-full px-2 py-0.5">Influencer</span>}
                        </td>
                        <td className="py-2 pr-4 text-[#78716C] text-xs">{s.email}</td>
                        <td className="py-2 pr-4 text-[#2C2A29]">{s.orders}</td>
                        <td className="py-2 text-[#2C2A29] font-medium">{formatPrice(s.gmv)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <p className="text-sm font-body text-[#78716C]">No seller activity in this date range.</p>}
            </div>

            {/* 4. Returns Analysis */}
            <div className="bg-white rounded-2xl p-6 border border-cream-100/50" data-testid="report-returns-analysis">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl text-[#2C2A29]">Returns Analysis</h3>
                  <p className="text-xs font-body text-[#78716C] mt-1">Return volume, reasons, and dispute outcomes.</p>
                </div>
                <button
                  onClick={() => downloadCsv('returns-analysis', 'returns-analysis')}
                  className="text-xs font-body text-[#9CAF88] border border-[#9CAF88]/40 hover:bg-[#9CAF88]/10 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                  data-testid="download-returns-analysis-csv"
                ><Download size={12} /> Download CSV</button>
              </div>
              {returnsAnalysis ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Total returns', value: returnsAnalysis.total_returns },
                      { label: 'Return rate', value: `${returnsAnalysis.return_rate_pct}%`, tone: returnsAnalysis.return_rate_pct > 5 ? 'rose' : 'emerald' },
                      { label: 'Disputed', value: returnsAnalysis.disputed },
                      { label: 'Refund total', value: formatPrice(returnsAnalysis.refund_total), tone: 'rose' },
                    ].map(m => (
                      <div key={m.label} className="bg-cream-50 rounded-xl p-3">
                        <p className="text-xs font-body text-[#78716C]">{m.label}</p>
                        <p className={`text-lg font-heading ${m.tone === 'emerald' ? 'text-emerald-600' : m.tone === 'rose' ? 'text-rose-600' : 'text-[#2C2A29]'}`}>{m.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-body text-[#78716C] mb-2">By reason</p>
                      {Object.keys(returnsAnalysis.by_reason || {}).length === 0
                        ? <p className="text-sm text-[#78716C]">—</p>
                        : Object.entries(returnsAnalysis.by_reason).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(
                          <div key={k} className="flex justify-between border-b border-cream-50 py-1.5 text-sm">
                            <span className="text-[#2C2A29]">{k.replace(/_/g, ' ')}</span><span className="text-[#78716C]">{v}</span>
                          </div>
                        ))}
                    </div>
                    <div>
                      <p className="text-xs font-body text-[#78716C] mb-2">By status</p>
                      {Object.keys(returnsAnalysis.by_status || {}).length === 0
                        ? <p className="text-sm text-[#78716C]">—</p>
                        : Object.entries(returnsAnalysis.by_status).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(
                          <div key={k} className="flex justify-between border-b border-cream-50 py-1.5 text-sm">
                            <span className="text-[#2C2A29]">{k.replace(/_/g, ' ')}</span><span className="text-[#78716C]">{v}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ) : <p className="text-sm font-body text-[#78716C]">No data.</p>}
            </div>

            {/* 5. Pending Payouts */}
            <div className="bg-white rounded-2xl p-6 border border-cream-100/50" data-testid="report-pending-payouts">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl text-[#2C2A29]">Pending Payouts</h3>
                  <p className="text-xs font-body text-[#78716C] mt-1">Snapshot — money owed to sellers right now (not date-filtered).</p>
                </div>
                <button
                  onClick={() => downloadCsv('pending-payouts', 'pending-payouts')}
                  className="text-xs font-body text-[#9CAF88] border border-[#9CAF88]/40 hover:bg-[#9CAF88]/10 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                  data-testid="download-pending-payouts-csv"
                ><Download size={12} /> Download CSV</button>
              </div>
              {pendingPayoutsReport ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-cream-50 rounded-xl p-3">
                      <p className="text-xs font-body text-[#78716C]">Gross sales (excl. shipping)</p>
                      <p className="text-lg font-heading text-[#2C2A29]">{formatPrice(pendingPayoutsReport.total_gross_sale || 0)}</p>
                    </div>
                    <div className="bg-cream-50 rounded-xl p-3">
                      <p className="text-xs font-body text-[#78716C]">Platform fee ({Math.round((pendingPayoutsReport.platform_fee_pct || 0.05) * 100)}%)</p>
                      <p className="text-lg font-heading text-emerald-700">{formatPrice(pendingPayoutsReport.total_platform_fee || 0)}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                      <p className="text-xs font-body text-emerald-700">Net owed to sellers</p>
                      <p className="text-lg font-heading text-emerald-800">{formatPrice(pendingPayoutsReport.total_owed)}</p>
                    </div>
                    <div className="bg-cream-50 rounded-xl p-3">
                      <p className="text-xs font-body text-[#78716C]">Orders / oldest</p>
                      <p className="text-sm font-body text-[#2C2A29]">
                        {pendingPayoutsReport.total_orders} order(s)
                      </p>
                      <p className="text-xs font-body text-[#78716C]">
                        {pendingPayoutsReport.oldest_completed_at ? new Date(pendingPayoutsReport.oldest_completed_at).toLocaleDateString('id-ID') : '—'}
                      </p>
                    </div>
                  </div>
                  {(pendingPayoutsReport.by_seller || []).length > 0 && (
                    <div>
                      <p className="text-xs font-body text-[#78716C] mb-2">Top recipients (by net amount owed)</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm font-body">
                          <thead><tr className="text-left text-[#78716C] border-b border-cream-100">
                            <th className="py-2 pr-4">Seller</th>
                            <th className="py-2 pr-4">Email</th>
                            <th className="py-2 pr-4">Orders</th>
                            <th className="py-2 pr-4">Gross</th>
                            <th className="py-2 pr-4">Fee</th>
                            <th className="py-2">Net payout</th>
                          </tr></thead>
                          <tbody>{pendingPayoutsReport.by_seller.slice(0, 10).map(s => (
                            <tr key={s.seller_id} className="border-b border-cream-50">
                              <td className="py-2 pr-4 text-[#2C2A29]">{s.name}</td>
                              <td className="py-2 pr-4 text-[#78716C] text-xs">{s.email}</td>
                              <td className="py-2 pr-4 text-[#2C2A29]">{s.orders}</td>
                              <td className="py-2 pr-4 text-[#78716C]">{formatPrice(s.gross_sale || 0)}</td>
                              <td className="py-2 pr-4 text-red-600">− {formatPrice(s.platform_fee || 0)}</td>
                              <td className="py-2 text-emerald-700 font-medium">{formatPrice(s.amount)}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : <p className="text-sm font-body text-[#78716C]">No data.</p>}
            </div>
          </div>
        )}

        {activeTab === 'verifications' && (
        <>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-6 animate-pulse border border-cream-100/50">
                <div className="flex items-center gap-4"><div className="w-14 h-14 rounded-full bg-cream-100" /><div className="flex-1 space-y-2"><div className="h-5 bg-cream-100 rounded w-40" /><div className="h-4 bg-cream-100 rounded w-28" /></div></div>
              </div>
            ))}
          </div>
        ) : pending.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-pending">
            <ShieldCheck size={48} className="mx-auto text-cream-200 mb-4" />
            <h3 className="font-heading text-xl text-[#2C2A29] mb-2">All caught up!</h3>
            <p className="font-body text-[#78716C]">No pending verification requests</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="pending-list">
            {pending.map(v => (
              <div key={v.user_id} className="bg-white rounded-2xl p-6 border border-cream-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`verification-card-${v.user_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* User Info */}
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {v.picture ? (
                      <img src={v.picture} alt={v.name} className="w-14 h-14 rounded-full object-cover border-2 border-cream-100 flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-blush-200 flex items-center justify-center text-xl font-heading font-bold text-[#2C2A29] flex-shrink-0">{v.name?.[0]}</div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-body font-semibold text-[#2C2A29] text-lg">{v.name}</h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {v.social_platforms?.[0] && (
                          <a href={platformUrl(v.social_platforms[0], v.social_handle)}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs font-body text-[#C5A880] hover:text-[#B89970] transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`social-link-${v.user_id}`}>
                            {v.social_platforms[0]}: {v.social_handle} <ExternalLink size={10} />
                          </a>
                        )}
                        {v.follower_range && (
                          <span className="text-xs font-body text-[#78716C] bg-cream-50 px-2 py-0.5 rounded-full">{v.follower_range} followers</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs font-body text-[#A89F91]">
                        <span className="flex items-center gap-1"><Clock size={10} /> Submitted {formatTime(v.verification_submitted_at)}</span>
                        {v.last_reminder_sent && (
                          <span className="flex items-center gap-1 text-amber-500"><Bell size={10} /> Reminder {formatTime(v.last_reminder_sent)}</span>
                        )}
                      </div>
                      {v.verification_code && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs font-body text-[#78716C]">Secret code:</span>
                          <code className="bg-amber-50 border border-amber-200 rounded px-2 py-0.5 font-mono text-xs font-bold text-amber-800 tracking-wider" data-testid={`admin-code-${v.user_id}`}>{v.verification_code}</code>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    <button
                      onClick={() => handleApprove(v.user_id, v.name)}
                      disabled={processing === v.user_id}
                      className="bg-green-500 text-white rounded-full px-5 py-2.5 font-body text-sm font-semibold transition-all hover:bg-green-600 hover:shadow-md disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`approve-btn-${v.user_id}`}>
                      <ShieldCheck size={14} /> Approve
                    </button>
                    <button
                      onClick={() => setRejectDialog({ open: true, userId: v.user_id, name: v.name })}
                      disabled={processing === v.user_id}
                      className="border border-red-200 text-red-500 rounded-full px-5 py-2.5 font-body text-sm font-semibold transition-all hover:bg-red-50 hover:border-red-300 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`reject-btn-${v.user_id}`}>
                      <ShieldX size={14} /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {/* Pending Payment Orders Tab */}
        {activeTab === 'orders' && (
          <>
          {pendingOrders.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-pending-orders">
              <Package size={48} className="mx-auto text-cream-200 mb-4" />
              <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No pending payments</h3>
              <p className="font-body text-[#78716C]">All orders are settled</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="pending-orders-list">
              {pendingOrders.map(o => {
                const productName = o.product_name || (o.items && o.items[0]?.product_name) || 'Order';
                const brand = o.brand || (o.items && o.items[0]?.brand) || '';
                const photo = o.photo || (o.items && o.items[0]?.photo);
                const amount = o.amount || o.total_amount || 0;
                return (
                  <div key={o.order_id} className="bg-white rounded-2xl p-5 border border-cream-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`admin-order-${o.order_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <OrderItemsBlock
                          items={o.items}
                          fallbackPhoto={photo}
                          fallbackBrand={brand}
                          fallbackProductName={productName}
                          orderId={o.order_id}
                        />
                        <div className="flex-shrink-0">
                          <p className="font-body text-sm font-semibold text-[#C5A880]">{formatPrice(amount)}</p>
                        </div>
                      </div>
                      <div className="text-left sm:text-right space-y-1 flex-shrink-0">
                        <p className="text-xs font-body text-[#78716C]">Buyer: <span className="font-medium text-[#2C2A29]">{o.buyer_name}</span></p>
                        <p className="text-xs font-body text-[#78716C]">Seller: <span className="font-medium text-[#2C2A29]">{o.seller_name}</span></p>
                        <p className="text-xs font-body text-[#A89F91]">{formatTime(o.created_at)}</p>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body font-semibold bg-yellow-100 text-yellow-800">
                          <Clock size={12} /> Pending Payment
                        </span>
                      </div>
                      <button onClick={() => setCancelDialog({ open: true, orderId: o.order_id, productName: productName })}
                        disabled={processing === o.order_id}
                        className="bg-red-500 text-white rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0"
                        data-testid={`cancel-order-${o.order_id}`}>
                        <XCircle size={14} /> Cancel Order
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
        )}

        {/* Refunds Queue — orders where refund_required=true (ship-timeout + seller-cancel approvals, etc.) */}
        {activeTab === 'refunds' && (
          <>
          {refundRequests.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-refund-requests">
              <RotateCcw size={48} className="mx-auto text-cream-200 mb-4" />
              <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No pending refunds</h3>
              <p className="font-body text-[#78716C]">All refund obligations have been cleared</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="refund-requests-list">
              <p className="text-xs font-body text-[#78716C] px-1">
                Orders awaiting manual refund. Transfer the money to the buyer (via Duitku dashboard or bank transfer), then click <strong>Mark Refunded</strong> to clear this queue.
              </p>
              {refundRequests.map(r => {
                const reasonLabel = r.refund_required_reason === 'dispute_rejected' ? 'Dispute rejected — buyer wins (admin decision)'
                  : r.cancellation_reason === 'seller_ship_timeout' ? 'Seller ship-by timeout (auto-cancelled)'
                  : r.cancellation_reason === 'seller_cancel_approved' ? 'Seller cancellation approved by admin'
                  : r.cancellation_reason || 'Refund required';
                return (
                <div key={r.order_id} className="bg-white rounded-2xl p-5 border border-amber-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`refund-card-${r.order_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="min-w-0 flex-1">
                        <OrderItemsBlock
                          items={r.items}
                          fallbackPhoto={r.photo}
                          fallbackBrand={r.brand}
                          fallbackProductName={r.product_name}
                          orderId={r.order_id}
                        />
                        <p className="font-body text-sm font-semibold text-[#C5A880] mt-2">{formatPrice(r.amount)}</p>
                        <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2">
                          <p className="text-xs font-body font-medium text-amber-700 mb-0.5">Reason:</p>
                          <p className="text-xs font-body text-amber-600">{reasonLabel}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-left sm:text-right space-y-1 flex-shrink-0">
                      <p className="text-xs font-body text-[#78716C]">Buyer: <span className="font-medium text-[#2C2A29]">{r.buyer_name}</span></p>
                      {r.buyer_email && <p className="text-xs font-body text-[#A89F91]">{r.buyer_email}</p>}
                      <p className="text-xs font-body text-[#78716C]">Seller: <span className="font-medium text-[#2C2A29]">{r.seller_name}</span></p>
                      <p className="text-xs font-body text-[#A89F91]">{formatTime(r.cancelled_at)}</p>
                      <div className="flex items-center gap-2 mt-2 sm:justify-end">
                        <button onClick={async () => {
                          try {
                            const { data } = await axios.post(`${API}/admin/messages/open`, { user_id: r.buyer_id });
                            navigate(`/chat/${data.conversation_id}`);
                          } catch (err) { toast.error(err.response?.data?.detail || 'Failed to open DM'); }
                        }}
                          className="border border-cream-100 text-[#78716C] rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-cream-50"
                          data-testid={`dm-buyer-${r.order_id}`}>
                          DM Buyer
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm(`Confirm you've transferred the refund to the buyer for order #${r.order_id}. This marks the order as refunded and clears it from the queue.`)) return;
                          setProcessing(r.order_id);
                          try {
                            await axios.post(`${API}/admin/orders/${r.order_id}/mark-refunded`);
                            toast.success(`Order #${r.order_id} marked as refunded`);
                            fetchRefundRequests();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                          finally { setProcessing(null); }
                        }}
                          disabled={processing === r.order_id}
                          className="bg-emerald-500 text-white rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-1.5"
                          data-testid={`mark-refunded-${r.order_id}`}>
                          <CheckCircle2 size={14} /> Mark Refunded
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          )}
          </>
        )}

        {/* Cancellation Requests Tab */}
        {activeTab === 'cancellations' && (
          <>
          {cancelRequests.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-cancel-requests">
              <XCircle size={48} className="mx-auto text-cream-200 mb-4" />
              <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No cancellation requests</h3>
              <p className="font-body text-[#78716C]">All cancellation requests have been resolved</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="cancel-requests-list">
              {cancelRequests.map(r => (
                <div key={r.cancel_id} className="bg-white rounded-2xl p-5 border border-red-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`cancel-card-${r.cancel_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="min-w-0 flex-1">
                        <OrderItemsBlock
                          items={r.items}
                          fallbackPhoto={r.photo}
                          fallbackBrand={r.brand}
                          fallbackProductName={r.product_name}
                          orderId={r.order_id}
                        />
                        <p className="font-body text-sm font-semibold text-[#C5A880] mt-2">{formatPrice(r.order_amount || r.amount)}</p>
                        {r.shipping_option && (
                          <p className="text-xs font-body text-blue-600 mt-1">Buyer's courier: {r.shipping_option}</p>
                        )}
                        <div className="mt-2 bg-red-50 rounded-lg px-3 py-2">
                          <p className="text-xs font-body font-medium text-red-700 mb-0.5">Reason:</p>
                          <p className="text-xs font-body text-red-600">{r.reason}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-left sm:text-right space-y-1 flex-shrink-0">
                      <p className="text-xs font-body text-[#78716C]">Seller: <span className="font-medium text-[#2C2A29]">{r.seller_name}</span></p>
                      <p className="text-xs font-body text-[#78716C]">Buyer: <span className="font-medium text-[#2C2A29]">{r.buyer_name}</span></p>
                      <p className="text-xs font-body text-[#A89F91]">{formatTime(r.created_at)}</p>
                      <div className="flex items-center gap-2 mt-2 sm:justify-end">
                        <button onClick={async () => {
                          if (!window.confirm('Delete this cancellation request from the queue? Testing helper — no money moved.')) return;
                          try {
                            await axios.delete(`${API}/admin/cancellations/${r.order_id}`);
                            toast.success('Cancellation request deleted');
                            fetchCancelRequests();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                        }}
                          className="border border-red-200 text-red-600 rounded-full px-3 py-2 font-body text-xs font-semibold transition-all hover:bg-red-50"
                          data-testid={`delete-cancel-${r.cancel_id}`} title="Delete (testing)">
                          <Trash2 size={13} />
                        </button>
                        <button onClick={() => handleCancelAction(r.cancel_id, 'dismiss')}
                          disabled={processing === r.cancel_id}
                          className="border border-cream-100 text-[#78716C] rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-cream-50 disabled:opacity-50"
                          data-testid={`dismiss-cancel-${r.cancel_id}`}>
                          Dismiss
                        </button>
                        <button onClick={() => handleCancelAction(r.cancel_id, 'approve')}
                          disabled={processing === r.cancel_id}
                          className="bg-red-500 text-white rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5"
                          data-testid={`approve-cancel-${r.cancel_id}`}>
                          <CheckCircle2 size={14} /> Approve
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
        )}



        {/* Returns Tab */}
        {activeTab === 'returns' && (
          <>
          {returnRequests.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-return-requests">
              <PackageOpen size={48} className="mx-auto text-cream-200 mb-4" />
              <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No return requests</h3>
              <p className="font-body text-[#78716C]">All return requests have been resolved</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="return-requests-list">
              {returnRequests.map(r => {
                const reasonLabel = { wrong_product: 'Wrong product sent', missing_items: 'Missing items', damaged_packing: 'Damaged due to poor packing', condition_different: 'Condition very different from listing', package_never_arrived: 'Package never arrived' }[r.reason] || r.reason;
                const statusLabel = { pending: 'Pending Review', approved: 'Approved — Awaiting Return Ship', return_shipped: 'Return Shipped', return_received: 'Return Received — Process Refund', refunded: 'Refunded', rejected: 'Rejected' }[r.status] || r.status;
                const statusColor = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-blue-100 text-blue-800', return_shipped: 'bg-purple-100 text-purple-800', return_received: 'bg-green-100 text-green-800', refunded: 'bg-green-200 text-green-900', rejected: 'bg-red-100 text-red-800' }[r.status] || 'bg-gray-100 text-gray-800';
                return (
                <div key={r.return_id} className="bg-white rounded-2xl p-5 border border-orange-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`return-card-${r.return_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs font-body text-[#A89F91] font-mono">#{r.order_id}</p>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-body font-semibold mt-1 ${statusColor}`}>
                        {statusLabel}
                      </span>
                    </div>
                    <p className="text-xs font-body text-[#A89F91]">{r.created_at ? new Date(r.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
                  </div>

                  <div className="flex gap-3 mb-3">
                    {r.photo && <img src={r.photo} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      {r.brand && <p className="text-[10px] font-body uppercase tracking-wider text-[#A89F91]">{r.brand}</p>}
                      <p className="font-body font-semibold text-[#2C2A29] text-sm">{r.product_name || 'Item'}</p>
                      <p className="text-xs font-body text-[#78716C]">Buyer: <span className="font-medium">{r.buyer_name}</span> &middot; Seller: <span className="font-medium">{r.seller_name}</span></p>
                      <p className="text-sm font-body font-semibold text-orange-700 mt-1">Refund: {formatPrice(r.item_price || 0)} + {formatPrice(r.shipping_cost || 0)} = {formatPrice(r.refund_amount || 0)}</p>
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-xl px-3 py-2 mb-3">
                    <p className="text-xs font-body font-medium text-orange-800 mb-0.5">Reason: {reasonLabel}</p>
                    {r.reason_detail && <p className="text-xs font-body text-orange-700">{r.reason_detail}</p>}
                  </div>

                  {/* Buyer Evidence */}
                  <div className="mb-3">
                    <p className="text-[10px] font-body font-bold uppercase tracking-wider text-blue-600 mb-1.5">Buyer Evidence</p>
                    <div className="flex flex-wrap gap-2">
                      {r.video_url && (
                        <a href={r.video_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-full px-3 py-1.5 text-xs font-body font-medium hover:bg-blue-100 transition-colors">
                          <Video size={12} /> View Unboxing Video
                        </a>
                      )}
                      {(r.photo_urls || []).map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt="" className="w-12 h-12 rounded-lg object-cover border border-blue-100 hover:opacity-80 transition-opacity" />
                        </a>
                      ))}
                      {!r.video_url && <span className="text-xs font-body text-red-500 font-medium">No video submitted</span>}
                    </div>
                  </div>

                  {/* Seller Dispute */}
                  {r.seller_dispute && (
                    <div className="mb-3">
                      <p className="text-[10px] font-body font-bold uppercase tracking-wider text-red-600 mb-1.5">Seller Dispute</p>
                      <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-2">
                        <p className="text-xs font-body font-medium text-red-800 mb-0.5">
                          Reason: {{ item_worse_condition: 'Item returned in worse condition', wrong_item_returned: 'Wrong/different item sent back', empty_package: 'Empty or missing package', product_used: 'Product used/consumed before returning' }[r.seller_dispute.reason] || r.seller_dispute.reason}
                        </p>
                        {r.seller_dispute.reason_detail && <p className="text-xs font-body text-red-700">{r.seller_dispute.reason_detail}</p>}
                        <p className="text-xs font-body text-red-400 mt-1">Filed: {r.seller_dispute.created_at ? new Date(r.seller_dispute.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(r.seller_dispute.photo_urls || []).map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt="" className="w-12 h-12 rounded-lg object-cover border border-red-100 hover:opacity-80 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Return tracking info */}
                  {r.return_courier && r.return_tracking_number && (
                    <div className="bg-purple-50 rounded-xl px-3 py-2 mb-3 text-xs font-body" data-testid={`admin-return-awb-${r.return_id}`}>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span><span className="text-purple-600">Return courier: </span><span className="text-purple-800 font-medium">{r.return_courier}</span></span>
                        <span><span className="text-purple-600">Tracking: </span><span className="text-purple-800 font-medium">{r.return_tracking_number}</span></span>
                        {r.return_auto_confirmed && <span className="text-purple-500">(Auto-confirmed)</span>}
                      </div>
                      {r.return_awb_format_overridden && (
                        <div className="mt-1.5 inline-flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 text-[11px] font-body text-amber-900" data-testid={`return-awb-override-flag-${r.return_id}`}>
                          <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                          <span><span className="font-semibold">Return AWB format overridden</span> — verify on courier site before resolving.</span>
                        </div>
                      )}
                      {r.return_shipment_proof_url && (
                        <div className="mt-2">
                          <p className="text-[11px] text-purple-700 uppercase tracking-wide font-semibold mb-1">Shipment proof photo</p>
                          <a
                            href={r.return_shipment_proof_url.startsWith('http') ? r.return_shipment_proof_url : `${process.env.REACT_APP_BACKEND_URL}${r.return_shipment_proof_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`return-shipment-proof-link-${r.return_id}`}
                            className="inline-block"
                          >
                            <img
                              src={r.return_shipment_proof_url.startsWith('http') ? r.return_shipment_proof_url : `${process.env.REACT_APP_BACKEND_URL}${r.return_shipment_proof_url}`}
                              alt="Return shipment proof"
                              className="max-h-32 rounded-lg border border-purple-200 object-cover bg-white"
                            />
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {r.admin_note && (
                    <div className="bg-cream-50 rounded-xl px-3 py-2 mb-3 text-xs font-body text-[#78716C]">
                      Admin note: {r.admin_note}
                    </div>
                  )}

                  {/* Action buttons based on status */}
                  {r.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setReturnDecisionDialog({ open: true, ret: r, action: 'reject' }); setReturnAdminNote(''); }}
                        disabled={processing === r.return_id}
                        className="border border-cream-100 text-[#78716C] rounded-full px-4 py-2 font-body text-sm font-semibold hover:bg-cream-50 disabled:opacity-50"
                        data-testid={`reject-return-${r.return_id}`}>
                        Reject
                      </button>
                      <button onClick={() => { setReturnDecisionDialog({ open: true, ret: r, action: 'approve_refund_only' }); setReturnAdminNote(''); }}
                        disabled={processing === r.return_id}
                        className="bg-green-100 text-green-800 rounded-full px-4 py-2 font-body text-sm font-semibold hover:bg-green-200 disabled:opacity-50"
                        data-testid={`approve-refund-only-${r.return_id}`}>
                        Refund Only
                      </button>
                      <button onClick={() => { setReturnDecisionDialog({ open: true, ret: r, action: 'approve_return_required' }); setReturnAdminNote(''); }}
                        disabled={processing === r.return_id}
                        className="bg-orange-100 text-orange-800 rounded-full px-4 py-2 font-body text-sm font-semibold hover:bg-orange-200 disabled:opacity-50"
                        data-testid={`approve-return-required-${r.return_id}`}>
                        Require Return
                      </button>
                    </div>
                  )}
                  {r.status === 'return_shipped' && !r.return_delivered_at && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Mark this return shipment as delivered to the seller? Use this only if tracking has clearly failed to auto-update. This will advance the status to "Return Received".')) return;
                        setProcessing(r.return_id);
                        try {
                          await axios.post(`${API}/admin/returns/${r.return_id}/mark-delivered`);
                          toast.success('Return marked as delivered to seller.');
                          fetchReturnRequests();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to mark delivered');
                        } finally {
                          setProcessing(null);
                        }
                      }}
                      disabled={processing === r.return_id}
                      className="bg-purple-500 text-white rounded-full px-6 py-2 font-body text-sm font-semibold hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`mark-return-delivered-${r.return_id}`}>
                      <Truck size={14} /> Mark Return Delivered (fallback)
                    </button>
                  )}
                  {r.status === 'return_received' && !r.seller_dispute && (
                    <button onClick={() => handleReturnAction(r.return_id, 'process-refund')}
                      disabled={processing === r.return_id}
                      className="bg-green-500 text-white rounded-full px-6 py-2 font-body text-sm font-semibold hover:bg-green-600 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`process-refund-${r.return_id}`}>
                      <CheckCircle2 size={14} /> Process Refund ({formatPrice(r.item_price || 0)} + {formatPrice(r.shipping_cost || 0)})
                    </button>
                  )}

                  {/* Dispute resolution buttons — seller disputed and admin hasn't resolved yet */}
                  {(r.status === 'return_shipped' || r.status === 'return_received') && r.seller_dispute && !r.dispute_resolution && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setReturnDecisionDialog({ open: true, ret: r, action: 'dispute_reject' }); setReturnAdminNote(''); }}
                        disabled={processing === r.return_id}
                        className="bg-emerald-500 text-white rounded-full px-4 py-2 font-body text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50"
                        data-testid={`reject-dispute-${r.return_id}`}>
                        Reject Dispute → Refund Buyer
                      </button>
                      <button onClick={() => { setReturnDecisionDialog({ open: true, ret: r, action: 'dispute_accept' }); setReturnAdminNote(''); }}
                        disabled={processing === r.return_id}
                        className="bg-purple-500 text-white rounded-full px-4 py-2 font-body text-sm font-semibold hover:bg-purple-600 disabled:opacity-50"
                        data-testid={`accept-dispute-${r.return_id}`}>
                        Accept Dispute → Pay Seller
                      </button>
                    </div>
                  )}

                  {/* Dispute rejected — order is now in the Refund Buyer queue.
                      Show a chip + jump link so the admin's mental model is:
                      decision = enqueue, money action = different tab. */}
                  {r.status === 'dispute_rejected' && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('refunds');
                        // Tabs live at the top of the page — without an
                        // explicit scroll, the admin's viewport stays
                        // anchored to the Returns card they just clicked
                        // and the new tab content (rendered above) is
                        // entirely off-screen, making the click feel
                        // unresponsive.
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        toast.success('Refund Buyer tab opened');
                      }}
                      className="inline-flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-3 py-1.5 text-xs font-body font-medium transition-all"
                      data-testid={`go-refund-buyer-${r.return_id}`}
                    >
                      <CheckCircle2 size={12} /> Moved to Refund Buyer queue — open tab →
                    </button>
                  )}

                  {/* Dispute accepted — order is now in the Pay Seller queue. */}
                  {r.status === 'dispute_accepted' && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('payouts');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        toast.success('Pay Seller tab opened');
                      }}
                      className="inline-flex items-center gap-1.5 bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200 rounded-full px-3 py-1.5 text-xs font-body font-medium transition-all"
                      data-testid={`go-pay-seller-${r.return_id}`}
                    >
                      <CheckCircle2 size={12} /> Moved to Pay Seller queue — open tab →
                    </button>
                  )}

                  {/* Terminal dispute states — informational chip */}
                  {r.status === 'seller_paid' && (
                    <span className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 rounded-full px-3 py-1 text-xs font-body font-medium">
                      <CheckCircle2 size={12} /> Seller paid — order closed
                    </span>
                  )}

                  {/* Delete (testing helper) — always available */}
                  <button onClick={async () => {
                    if (!window.confirm('Delete this return request from the queue? This also clears has_return_request on the order. Testing helper — no money moved.')) return;
                    try {
                      await axios.delete(`${API}/admin/returns/${r.return_id}`);
                      toast.success('Return request deleted');
                      fetchReturnRequests();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }}
                    className="mt-2 border border-red-200 text-red-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold transition-all hover:bg-red-50 flex items-center gap-1"
                    data-testid={`delete-return-${r.return_id}`}>
                    <Trash2 size={12} /> Delete (testing)
                  </button>
                </div>
                );
              })}
            </div>
          )}
          </>
        )}

        {/* Return Decision Dialog */}
        <Dialog open={returnDecisionDialog.open} onOpenChange={(v) => { if (!v) setReturnDecisionDialog({ open: false, ret: null, action: '' }); }}>
          <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-md mx-4" data-testid="return-decision-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#2C2A29]">
                {returnDecisionDialog.action === 'reject' && 'Reject Return'}
                {returnDecisionDialog.action === 'approve_refund_only' && 'Approve — Refund Only'}
                {returnDecisionDialog.action === 'approve_return_required' && 'Approve — Require Return'}
                {returnDecisionDialog.action === 'dispute_accept' && 'Accept Seller\'s Dispute'}
                {returnDecisionDialog.action === 'dispute_reject' && 'Reject Seller\'s Dispute'}
              </DialogTitle>
              <DialogDescription className="font-body text-[#78716C]">
                {returnDecisionDialog.ret?.product_name} &middot; {formatPrice(returnDecisionDialog.ret?.refund_amount || 0)}
                {returnDecisionDialog.action === 'dispute_accept' && ' · Seller keeps item price, Stashmart retains shipping fee.'}
                {returnDecisionDialog.action === 'dispute_reject' && ' · Buyer refunded item price + shipping.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="block text-xs font-body font-medium text-[#78716C] mb-1">
                  Admin note{['dispute_accept', 'dispute_reject', 'reject'].includes(returnDecisionDialog.action) ? ' (required — shown to both parties)' : ' (optional)'}
                </label>
                <textarea value={returnAdminNote} onChange={e => setReturnAdminNote(e.target.value)}
                  placeholder={['dispute_accept', 'dispute_reject', 'reject'].includes(returnDecisionDialog.action)
                    ? 'Explain the decision so the buyer and seller understand what happens next...'
                    : 'Internal note...'}
                  className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent resize-none"
                  rows={3} data-testid="return-admin-note" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setReturnDecisionDialog({ open: false, ret: null, action: '' })}
                  className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Cancel</button>
                <button onClick={() => {
                  const ret = returnDecisionDialog.ret;
                  const act = returnDecisionDialog.action;
                  if (['dispute_accept', 'dispute_reject', 'reject'].includes(act) && !returnAdminNote.trim()) {
                    toast.error(act === 'reject' ? 'Admin note is required when rejecting a return' : 'Admin note is required for dispute decisions');
                    return;
                  }
                  if (act === 'reject') handleReturnAction(ret.return_id, 'reject');
                  else if (act === 'approve_refund_only') handleReturnAction(ret.return_id, 'approve', 'refund_only');
                  else if (act === 'approve_return_required') handleReturnAction(ret.return_id, 'approve', 'return_required');
                  else if (act === 'dispute_accept') handleReturnAction(ret.return_id, 'dispute_accept');
                  else if (act === 'dispute_reject') handleReturnAction(ret.return_id, 'dispute_reject');
                }}
                  className={`flex-1 rounded-full px-4 py-2.5 font-body font-semibold transition-all ${
                    returnDecisionDialog.action === 'reject' ? 'bg-red-500 text-white hover:bg-red-600' :
                    returnDecisionDialog.action === 'dispute_accept' || returnDecisionDialog.action === 'pay_seller' ? 'bg-purple-500 text-white hover:bg-purple-600' :
                    'bg-green-500 text-white hover:bg-green-600'
                  }`}
                  data-testid="return-decision-confirm-btn">
                  Confirm
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <>
          {payoutOrders.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-payouts">
              <CreditCard size={48} className="mx-auto text-cream-200 mb-4" />
              <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No pending payouts</h3>
              <p className="font-body text-[#78716C]">All seller payouts have been processed</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="payout-list">
              <div className="flex items-center justify-between mb-2">
                <p className="font-body text-sm text-[#78716C]">{payoutOrders.length} order(s) ready for payout</p>
                <p className="font-body text-sm font-semibold text-green-700">
                  Net to pay sellers: {formatPrice(payoutOrders.reduce((sum, o) => sum + (o.payout_amount || 0), 0))}
                </p>
              </div>
              {payoutOrders.map(o => (
                <div key={o.order_id} className="bg-white rounded-2xl p-4 border border-green-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`payout-${o.order_id}`}>
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <div className="min-w-0 flex-1">
                      <OrderItemsBlock
                        items={o.items}
                        fallbackPhoto={o.photo}
                        fallbackBrand={o.brand}
                        fallbackProductName={o.product_name || (o.items?.[0]?.product_name) || 'Order'}
                        orderId={o.order_id}
                        compact
                      />
                      {o.payout_via_dispute && (
                        <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-body font-semibold uppercase tracking-wide text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5" data-testid={`payout-via-dispute-${o.order_id}`}>
                          <ShieldCheck size={10} /> via dispute · seller won
                        </span>
                      )}
                    </div>
                    <p className="font-body font-semibold text-green-700 flex-shrink-0">{formatPrice(o.payout_amount || 0)}</p>
                  </div>
                  <div className="flex items-center justify-between text-xs font-body text-[#78716C]">
                    <span>Seller: <span className="font-medium text-[#2C2A29]">{o.seller_name || 'Unknown'}</span></span>
                    <span>Completed: {o.completed_at ? new Date(o.completed_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '—'}</span>
                  </div>
                  {/* Payout breakdown — shows how the final payout was calculated */}
                  <div className="mt-2 bg-cream-50 rounded-lg px-3 py-2 text-xs font-body" data-testid={`payout-breakdown-${o.order_id}`}>
                    <div className="flex items-center justify-between text-[#78716C]">
                      <span>Item subtotal (sale price)</span>
                      <span className="text-[#2C2A29] font-mono">{formatPrice(o.gross_sale || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[#78716C] mt-0.5">
                      <span>Platform fee ({Math.round((o.platform_fee_pct || 0.05) * 100)}%)</span>
                      <span className="text-red-600 font-mono">− {formatPrice(o.platform_fee || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 pt-1 border-t border-cream-100">
                      <span className="font-semibold text-[#2C2A29]">Payout to seller</span>
                      <span className="text-emerald-700 font-mono font-semibold">{formatPrice(o.payout_amount || 0)}</span>
                    </div>
                    {(o.shipping_cost || 0) > 0 && (
                      <p className="text-[11px] text-[#A89F91] italic mt-1.5">
                        Shipping {formatPrice(o.shipping_cost)} is platform-neutral (buyer pre-paid → courier already paid from Komerce wallet).
                      </p>
                    )}
                  </div>
                  {/* Seller payout-bank snapshot — pulled inline from /admin/payouts.
                      Admin can copy-paste directly into the bank transfer without
                      switching screens. Yellow warning if the seller hasn't saved
                      their bank details yet. */}
                  {o.seller_payout_bank ? (
                    <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs font-body" data-testid={`payout-bank-${o.order_id}`}>
                      <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-800 mb-1">Transfer to</p>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-blue-900 truncate">
                            {o.seller_payout_bank.bank_name === 'Other' && o.seller_payout_bank.bank_name_other
                              ? o.seller_payout_bank.bank_name_other
                              : o.seller_payout_bank.bank_name}
                          </p>
                          <p className="text-blue-900">A.n. {o.seller_payout_bank.account_holder}</p>
                          <p className="text-blue-900 tabular-nums font-mono">{o.seller_payout_bank.account_number}</p>
                        </div>
                        <button
                          onClick={async () => {
                            const txt = `${o.seller_payout_bank.bank_name === 'Other' ? o.seller_payout_bank.bank_name_other : o.seller_payout_bank.bank_name}\n${o.seller_payout_bank.account_holder}\n${o.seller_payout_bank.account_number}`;
                            try { await navigator.clipboard.writeText(txt); toast.success('Bank details copied'); }
                            catch { toast.error('Copy failed'); }
                          }}
                          className="text-[11px] font-body text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline inline-flex items-center gap-1 flex-shrink-0"
                          data-testid={`copy-payout-bank-${o.order_id}`}
                        >
                          <Copy size={11} /> Copy
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs font-body flex items-start gap-2" data-testid={`payout-bank-missing-${o.order_id}`}>
                      <AlertTriangle size={14} className="text-amber-700 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-amber-900">Bank details missing</p>
                        <p className="text-amber-800">Seller hasn't saved their bank account yet. DM them to add it before marking paid.</p>
                      </div>
                    </div>
                  )}
                  {/* AWB format override flag — admin should spot-check the
                      ship-proof photo before releasing payout if the seller
                      bypassed our pattern regex. */}
                  {o.awb_format_overridden && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs font-body flex items-start gap-2" data-testid={`awb-overridden-${o.order_id}`}>
                      <AlertTriangle size={14} className="text-amber-700 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-amber-900">AWB pattern override</p>
                        <p className="text-amber-800">Seller bypassed the courier format check. Spot-check the ship-proof photo against the resi before marking paid.</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/payouts/${o.order_id}/mark-paid`);
                        toast.success(`Payout marked for #${o.order_id}`);
                        setPayoutOrders(prev => prev.filter(p => p.order_id !== o.order_id));
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    }}
                      className="flex-1 bg-green-100 text-green-800 rounded-full px-4 py-2 text-sm font-body font-semibold hover:bg-green-200 transition-all flex items-center justify-center gap-1.5"
                      data-testid={`mark-paid-${o.order_id}`}>
                      <CheckCircle2 size={14} /> Mark as Paid to Seller
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm('Remove this order from the payout queue? Testing helper — no money moved, no fake payout recorded.')) return;
                      try {
                        await axios.delete(`${API}/admin/payouts/${o.order_id}`);
                        toast.success('Removed from payout queue');
                        setPayoutOrders(prev => prev.filter(p => p.order_id !== o.order_id));
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    }}
                      className="border border-red-200 text-red-600 rounded-full px-3 py-2 text-xs font-body font-semibold transition-all hover:bg-red-50"
                      data-testid={`delete-payout-${o.order_id}`} title="Delete (testing)">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
        )}

        {/* Listings Management Tab */}
        {activeTab === 'listings' && (
          <>
            {/* Search bar */}
            <div className="relative mb-4">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A89F91]" />
              <input
                type="text" value={listingsSearch}
                onChange={e => { setListingsSearch(e.target.value); setListingsPage(1); }}
                placeholder="Search listings by product name, brand, or description..."
                className="w-full bg-white border border-cream-100 rounded-full pl-11 pr-4 py-3 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                data-testid="admin-listings-search"
              />
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 mb-4">
              {[
                { value: 'active', label: 'Active' },
                { value: 'removed', label: 'Removed' },
                { value: 'all', label: 'All' },
              ].map(s => (
                <button key={s.value} onClick={() => { setListingsStatusFilter(s.value); setListingsPage(1); }}
                  className={`rounded-full px-4 py-1.5 text-xs font-body font-medium transition-all ${
                    listingsStatusFilter === s.value ? 'bg-[#2C2A29] text-white' : 'bg-white text-[#78716C] border border-cream-100 hover:border-[#2C2A29]'
                  }`} data-testid={`listings-filter-${s.value}`}>{s.label}</button>
              ))}
            </div>

            <p className="text-xs font-body text-[#A89F91] mb-4">{listingsTotal} listing{listingsTotal !== 1 ? 's' : ''}</p>

            {allListings.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-admin-listings">
                <Package size={48} className="mx-auto text-cream-200 mb-4" />
                <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No listings found</h3>
                <p className="font-body text-[#78716C]">{listingsSearch ? 'Try a different search term' : 'No listings in this view'}</p>
              </div>
            ) : (
              <>
              <div className="space-y-3" data-testid="admin-listings-list">
                {allListings.map(l => {
                  const isRemoved = l.admin_status === 'removed';
                  return (
                  <div key={l.listing_id} className={`bg-white rounded-2xl p-4 border ${isRemoved ? 'border-amber-200 bg-amber-50/20' : 'border-cream-100/50'} flex items-center gap-4`} data-testid={`admin-listing-${l.listing_id}`}>
                    {l.photos?.[0] ? (
                      <img src={l.photos[0]} alt="" className={`w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-cream-100 cursor-pointer ${isRemoved ? 'opacity-50' : ''}`}
                        onClick={() => navigate(`/listing/${l.listing_id}`)} />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-cream-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[10px] font-body uppercase tracking-wider text-[#A89F91]">{l.brand}</p>
                        {isRemoved && <span className="text-[10px] font-body font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-0.5"><EyeOff size={8} />REMOVED</span>}
                        {l.sold_out && (
                          <span className="text-[10px] font-body font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                            SOLD OUT{l.buyer_name ? ` to ${l.buyer_name}` : ''}
                          </span>
                        )}
                      </div>
                      <p className="font-body font-semibold text-[#2C2A29] text-sm truncate">{l.product_name}</p>
                      <p className="text-xs font-body text-[#78716C]">
                        by {l.seller_name} &middot; {formatPrice(l.price)} &middot; {l.condition}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      <button onClick={() => navigate(`/listing/${l.listing_id}`)}
                        className="border border-cream-100 text-[#2C2A29] rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-cream-50 transition-all flex items-center gap-1"
                        data-testid={`view-listing-${l.listing_id}`}>
                        <Eye size={12} /> View
                      </button>
                      {l.sold_out && !isRemoved && (
                        <button onClick={async () => {
                          if (!window.confirm(`Restock "${l.product_name}"? This sets sold_out=false and quantity=1 so the listing goes back on sale.`)) return;
                          setProcessing(l.listing_id);
                          try {
                            await axios.post(`${API}/admin/listings/${l.listing_id}/restock`);
                            toast.success('Listing restocked');
                            fetchListings();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                          finally { setProcessing(null); }
                        }}
                          disabled={processing === l.listing_id}
                          className="border border-emerald-300 text-emerald-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-emerald-50 transition-all disabled:opacity-50 flex items-center gap-1"
                          data-testid={`restock-listing-${l.listing_id}`}>
                          <RotateCcw size={12} /> Restock
                        </button>
                      )}
                      {isRemoved ? (
                        <button onClick={() => handleRestoreListing(l.listing_id)}
                          disabled={processing === l.listing_id}
                          className="border border-green-300 text-green-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-green-50 transition-all disabled:opacity-50 flex items-center gap-1"
                          data-testid={`restore-listing-${l.listing_id}`}>
                          <RotateCw size={12} /> Restore
                        </button>
                      ) : (
                        <button onClick={() => handleRemoveListing(l.listing_id)}
                          disabled={processing === l.listing_id}
                          className="border border-amber-300 text-amber-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-amber-50 transition-all disabled:opacity-50 flex items-center gap-1"
                          data-testid={`remove-listing-${l.listing_id}`}>
                          <EyeOff size={12} /> Remove
                        </button>
                      )}
                      <button onClick={() => setDeleteDialog({ open: true, listingId: l.listing_id, productName: `${l.brand} ${l.product_name}` })}
                        disabled={processing === l.listing_id}
                        className="border border-red-200 text-red-400 rounded-full p-1.5 hover:bg-red-50 hover:text-red-600 transition-all disabled:opacity-50"
                        data-testid={`delete-listing-${l.listing_id}`} title="Permanently delete">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {listingsTotal > 20 && (
                <div className="flex items-center justify-center gap-3 mt-6" data-testid="listings-pagination">
                  <button onClick={() => setListingsPage(p => Math.max(1, p - 1))} disabled={listingsPage <= 1}
                    className="border border-cream-100 rounded-full p-2 hover:bg-cream-50 disabled:opacity-30 transition-all">
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm font-body text-[#78716C]">Page {listingsPage} of {Math.ceil(listingsTotal / 20)}</span>
                  <button onClick={() => setListingsPage(p => p + 1)} disabled={listingsPage >= Math.ceil(listingsTotal / 20)}
                    className="border border-cream-100 rounded-full p-2 hover:bg-cream-50 disabled:opacity-30 transition-all">
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
              </>
            )}
          </>
        )}

        {/* Videos Tab — admin moderation of influencer video content. Mirrors
            the Listings tab pattern: search + status filter + per-row
            Remove/Restore/Delete actions. Removed videos disappear from every
            public surface (feed, detail, author profile, share links). */}
        {activeTab === 'videos' && (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A89F91]" />
                <input
                  type="text"
                  value={videosSearch}
                  onChange={e => { setVideosSearch(e.target.value); setVideosPage(1); }}
                  placeholder="Search videos by title or description..."
                  className="w-full bg-white border border-cream-100 rounded-full pl-11 pr-4 py-3 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                  data-testid="admin-videos-search"
                />
              </div>
              <div className="flex gap-1.5">
                {[
                  { value: 'active', label: 'Active' },
                  { value: 'removed', label: 'Removed' },
                  { value: 'all', label: 'All' },
                ].map(s => (
                  <button key={s.value} onClick={() => { setVideosStatusFilter(s.value); setVideosPage(1); }}
                    className={`rounded-full px-4 py-2 text-xs font-body font-semibold transition-all ${
                      videosStatusFilter === s.value ? 'bg-[#2C2A29] text-white' : 'bg-white text-[#78716C] border border-cream-100 hover:border-[#2C2A29]'
                    }`}
                    data-testid={`admin-videos-filter-${s.value}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs font-body text-[#A89F91] mb-4" data-testid="admin-videos-total">
              {videosTotal} video{videosTotal !== 1 ? 's' : ''}
            </p>
            {allVideos.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-3xl border border-cream-100/50" data-testid="no-videos">
                <Video size={32} className="mx-auto mb-3 text-[#A89F91]" />
                <p className="font-body text-[#78716C]">No videos to show in this filter</p>
              </div>
            ) : (
              <>
                <div className="grid gap-3">
                  {allVideos.map(v => {
                    const isRemoved = v.admin_status === 'removed';
                    return (
                      <div key={v.video_id}
                        className={`bg-white rounded-2xl p-4 border ${isRemoved ? 'border-red-200/60 opacity-80' : 'border-cream-100/50'} shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col sm:flex-row gap-4`}
                        data-testid={`admin-video-${v.video_id}`}
                      >
                        {/* Thumbnail */}
                        <div className="w-full sm:w-32 h-32 sm:h-32 flex-shrink-0 rounded-xl overflow-hidden bg-cream-100 relative">
                          {v.thumbnail_url ? (
                            <img src={v.thumbnail_url} alt={v.title || 'Video thumbnail'} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#A89F91]">
                              <Video size={32} />
                            </div>
                          )}
                          {isRemoved && (
                            <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
                              <span className="text-white text-xs font-body font-bold uppercase tracking-wider">Removed</span>
                            </div>
                          )}
                        </div>
                        {/* Meta + actions */}
                        <div className="flex-1 min-w-0 flex flex-col">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="font-body font-medium text-[#2C2A29] truncate" data-testid={`admin-video-title-${v.video_id}`}>
                              {v.title || 'Untitled video'}
                            </p>
                            {v.report_count > 0 && (
                              <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-body font-semibold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid={`admin-video-reports-${v.video_id}`}>
                                <Flag size={10} /> {v.report_count} pending report{v.report_count > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-body text-[#A89F91] mb-1">
                            by <span className="text-[#78716C] font-medium">{v.author_name}</span>
                            {v.author_email && <span className="ml-1">({v.author_email})</span>}
                          </p>
                          <p className="text-xs font-body text-[#A89F91] mb-2">
                            <Eye size={11} className="inline -mt-0.5 mr-1" />{v.views_count || 0} views ·
                            <Heart size={11} className="inline -mt-0.5 mx-1" />{v.likes_count || 0} likes ·
                            posted {new Date(v.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          {v.description && (
                            <p className="text-xs font-body text-[#78716C] mb-3 line-clamp-2 leading-relaxed">{v.description}</p>
                          )}
                          <div className="flex flex-wrap gap-1.5 mt-auto">
                            <a
                              href={v.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="border border-cream-100 text-[#78716C] rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-cream-50 transition-all flex items-center gap-1"
                              data-testid={`view-video-${v.video_id}`}
                            >
                              <ExternalLink size={12} /> Open video
                            </a>
                            {isRemoved ? (
                              <button onClick={() => handleRestoreVideo(v.video_id)}
                                disabled={processing === v.video_id}
                                className="border border-green-300 text-green-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-green-50 transition-all disabled:opacity-50 flex items-center gap-1"
                                data-testid={`restore-video-${v.video_id}`}
                              >
                                <RotateCw size={12} /> Restore
                              </button>
                            ) : (
                              <button onClick={() => handleRemoveVideo(v.video_id)}
                                disabled={processing === v.video_id}
                                className="border border-amber-300 text-amber-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-amber-50 transition-all disabled:opacity-50 flex items-center gap-1"
                                data-testid={`remove-video-${v.video_id}`}
                              >
                                <EyeOff size={12} /> Take down
                              </button>
                            )}
                            <button onClick={() => setVideoDeleteDialog({ open: true, videoId: v.video_id, title: v.title || 'Untitled video' })}
                              disabled={processing === v.video_id}
                              className="border border-red-200 text-red-400 rounded-full p-1.5 hover:bg-red-50 hover:text-red-600 transition-all disabled:opacity-50"
                              data-testid={`delete-video-${v.video_id}`}
                              title="Permanently delete (cannot be undone)"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {videosTotal > 20 && (
                  <div className="flex items-center justify-center gap-3 mt-6" data-testid="admin-videos-pager">
                    <button onClick={() => setVideosPage(p => Math.max(1, p - 1))} disabled={videosPage <= 1}
                      className="border border-cream-100 rounded-full p-2 hover:bg-cream-50 disabled:opacity-30 transition-all">
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-sm font-body text-[#78716C]">Page {videosPage} of {Math.ceil(videosTotal / 20)}</span>
                    <button onClick={() => setVideosPage(p => p + 1)} disabled={videosPage >= Math.ceil(videosTotal / 20)}
                      className="border border-cream-100 rounded-full p-2 hover:bg-cream-50 disabled:opacity-30 transition-all">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A89F91]" />
                <input type="text" value={ordersSearch}
                  onChange={e => { setOrdersSearch(e.target.value); setOrdersPage(1); }}
                  placeholder="Search by order ID..."
                  className="w-full bg-white border border-cream-100 rounded-full pl-11 pr-4 py-3 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                  data-testid="admin-orders-search" />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {['', 'pending', 'paid', 'shipped', 'completed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => { setOrdersStatusFilter(s); setOrdersPage(1); }}
                    className={`rounded-full px-3.5 py-2 text-xs font-body font-medium transition-all ${
                      ordersStatusFilter === s ? 'bg-[#F4C2C2] text-[#2C2A29]' : 'bg-cream-50 text-[#78716C] hover:bg-cream-100'
                    }`} data-testid={`filter-${s || 'all'}`}>
                    {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs font-body text-[#A89F91] mb-4">{ordersTotal} transaction{ordersTotal !== 1 ? 's' : ''}</p>

            {allOrders.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-transactions">
                <CreditCard size={48} className="mx-auto text-cream-200 mb-4" />
                <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No transactions</h3>
                <p className="font-body text-[#78716C]">{ordersSearch || ordersStatusFilter ? 'No results match your filters' : 'No transactions yet'}</p>
              </div>
            ) : (
              <>
              <div className="space-y-3" data-testid="admin-orders-list">
                {allOrders.map(o => {
                  const rawStatus = o.payment_status || o.status || 'unknown';
                  // Derive display: "shipped" before the courier picks up should
                  // read "Awaiting Pickup" — matches the seller-side UX. Only flip
                  // to real "Shipped" once Komerce webhook confirms picked_up/
                  // in_transit, or delivered.
                  const ps = (o.ro_pickup_status || '').toLowerCase();
                  const awaitingPickup = rawStatus === 'shipped' && !o.delivered_at &&
                    !['picked_up', 'in_transit', 'delivered'].includes(ps);
                  const status = rawStatus;
                  const displayLabel = awaitingPickup
                    ? 'Awaiting Pickup'
                    : (rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1));
                  const statusColors = {
                    pending: 'bg-yellow-100 text-yellow-800',
                    paid: 'bg-blue-100 text-blue-800',
                    shipped: 'bg-purple-100 text-purple-800',
                    completed: 'bg-green-100 text-green-800',
                    cancelled: 'bg-red-100 text-red-500',
                  };
                  const badgeClass = awaitingPickup
                    ? 'bg-amber-100 text-amber-800'
                    : (statusColors[status] || 'bg-cream-100 text-[#78716C]');
                  return (
                    <div key={o.order_id} className="bg-white rounded-2xl p-4 border border-cream-100/50 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`admin-txn-${o.order_id}`}>
                      <div className="flex-1 min-w-0">
                        <OrderItemsBlock
                          items={o.items}
                          fallbackPhoto={o.photo}
                          fallbackBrand={o.brand}
                          fallbackProductName={o.product_name}
                          orderId={o.order_id}
                          compact
                          dense
                        />
                        <p className="text-xs font-body text-[#78716C] mt-1">
                          {o.buyer_name} &rarr; {o.seller_name} &middot; {formatPrice(o.amount || o.total_amount || 0)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap sm:justify-end">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-body font-semibold ${badgeClass}`}>
                          {displayLabel}
                        </span>
                        <p className="text-xs font-body text-[#A89F91] hidden sm:block">{formatTime(o.created_at)}</p>
                        {status === 'pending' && (
                          <button onClick={() => setMarkPaidDialog({ open: true, orderId: o.order_id, amount: o.amount || 0, note: '' })}
                            disabled={processing === o.order_id}
                            className="bg-emerald-600 text-white rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center gap-1"
                            title="Mark as paid manually — use when gateway failed to report a real payment"
                            data-testid={`mark-paid-manual-${o.order_id}`}>
                            <CheckCircle2 size={12} /> Mark Paid
                          </button>
                        )}
                        {status === 'shipped' && !o.delivered_at && (
                          <button onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/orders/${o.order_id}/mark-delivered`);
                              toast.success(`Order #${o.order_id} marked as delivered`);
                              fetchAllOrders();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                          }}
                            disabled={processing === o.order_id}
                            className="bg-green-500 text-white rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-green-600 transition-all disabled:opacity-50 flex items-center gap-1"
                            data-testid={`mark-delivered-${o.order_id}`}>
                            <Truck size={12} /> Mark Delivered
                          </button>
                        )}
                        {status === 'shipped' && o.delivered_at && (
                          <span className="text-xs font-body text-green-600 font-medium flex items-center gap-1">
                            <CheckCircle2 size={12} /> Delivered
                          </span>
                        )}
                        {/* Shipment proof — mandatory upload from the seller's
                            Mark as Shipped flow. Admin can click to inspect
                            the photo before releasing payout. */}
                        {(status === 'shipped' || status === 'completed') && o.shipment_proof_url && (
                          <a
                            href={o.shipment_proof_url.startsWith('http') ? o.shipment_proof_url : `${process.env.REACT_APP_BACKEND_URL}${o.shipment_proof_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-full px-3 py-1.5 font-body text-xs font-semibold transition-all"
                            title="Open the seller's shipment proof photo in a new tab"
                            data-testid={`ship-proof-link-${o.order_id}`}
                          >
                            <Camera size={12} /> Ship proof
                          </a>
                        )}
                        {o.awb_format_overridden && (
                          <span
                            className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2 py-1 font-body text-[11px] font-semibold"
                            title="Seller submitted an AWB whose format didn't match our pattern. Spot-check the ship-proof photo before payout."
                            data-testid={`awb-overridden-${o.order_id}`}
                          >
                            <AlertTriangle size={11} /> AWB override
                          </span>
                        )}
                        {o.ro_order_no && (
                          <button onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/admin/orders/${o.order_id}/komerce-debug?reveal_key=true`);
                              setKomerceDebug({ open: true, orderId: o.order_id, data: res.data });
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load debug info'); }
                          }}
                            className="bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full px-3 py-1.5 font-body text-xs font-semibold transition-all flex items-center gap-1"
                            title="Show the exact curl we sent to Komerce — copy & share with their support team"
                            data-testid={`komerce-debug-${o.order_id}`}>
                            <Bug size={12} /> Komerce debug
                          </button>
                        )}
                        <button onClick={() => setDeleteOrderDialog({ open: true, orderId: o.order_id, orderId_display: o.order_id })}
                          disabled={processing === o.order_id}
                          className="bg-red-500 text-white rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center gap-1"
                          data-testid={`delete-order-${o.order_id}`}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {ordersTotal > 20 && (
                <div className="flex items-center justify-center gap-3 mt-6" data-testid="orders-pagination">
                  <button onClick={() => setOrdersPage(p => Math.max(1, p - 1))} disabled={ordersPage <= 1}
                    className="border border-cream-100 rounded-full p-2 hover:bg-cream-50 disabled:opacity-30 transition-all">
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm font-body text-[#78716C]">Page {ordersPage} of {Math.ceil(ordersTotal / 20)}</span>
                  <button onClick={() => setOrdersPage(p => p + 1)} disabled={ordersPage >= Math.ceil(ordersTotal / 20)}
                    className="border border-cream-100 rounded-full p-2 hover:bg-cream-50 disabled:opacity-30 transition-all">
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
              </>
            )}
          </>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <>
          {reportsLoading ? (
            <div className="space-y-4">
              {[1, 2].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse border border-cream-100/50">
                  <div className="flex items-center gap-4"><div className="w-12 h-12 rounded-lg bg-cream-100" /><div className="flex-1 space-y-2"><div className="h-5 bg-cream-100 rounded w-48" /><div className="h-4 bg-cream-100 rounded w-32" /></div></div>
                </div>
              ))}
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-reports">
              <Flag size={48} className="mx-auto text-cream-200 mb-4" />
              <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No reports</h3>
              <p className="font-body text-[#78716C]">No pending listing reports</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="reports-list">
              {reports.map(r => {
                const isVideo = r.content_type === 'video';
                const goPath = r.content_url_path || (isVideo ? `/videos/${r.video_id}` : `/listing/${r.listing_id}`);
                return (
                <div key={r.report_id} className="bg-white rounded-2xl p-6 border border-cream-100/50 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`report-card-${r.report_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      {r.listing_photo ? (
                        <img src={r.listing_photo} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-cream-100" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-cream-100 flex-shrink-0 flex items-center justify-center text-[#A89F91]">
                          {isVideo ? <Video size={20} /> : null}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-body font-semibold text-[#2C2A29]">{r.listing_name}</h3>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-body font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${isVideo ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blush-50 text-[#C46A6A] border-blush-200'}`}>
                            {isVideo ? <Video size={10} /> : <Eye size={10} />} {isVideo ? 'Video' : 'Listing'}
                          </span>
                        </div>
                        <p className="text-sm font-body mt-1">
                          <span className="text-red-500 font-medium">{r.reason}</span>
                          {r.details && <span className="text-[#78716C]"> — {r.details}</span>}
                        </p>
                        <p className="text-xs font-body text-[#A89F91] mt-1.5">
                          Reported by {r.reporter_name} · {formatTime(r.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                      <button onClick={() => navigate(goPath)}
                        className="border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-cream-50 flex items-center gap-1.5"
                        data-testid={`view-listing-${r.report_id}`}>
                        <Eye size={14} /> View
                      </button>
                      <button onClick={() => handleReportAction(r.report_id, 'dismiss')}
                        disabled={processing === r.report_id}
                        className="border border-cream-100 text-[#78716C] rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-cream-50 disabled:opacity-50"
                        data-testid={`dismiss-report-${r.report_id}`}>
                        Dismiss
                      </button>
                      <button onClick={() => handleReportAction(r.report_id, 'deactivate')}
                        disabled={processing === r.report_id}
                        className="bg-red-500 text-white rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`deactivate-listing-${r.report_id}`}>
                        <Trash2 size={14} /> {isVideo ? 'Take down' : 'Remove Listing'}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
          </>
        )}

        {/* Flags Tab */}
        {activeTab === 'flags' && (
          <>
          {flags.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-flags">
              <AlertTriangle size={48} className="mx-auto text-cream-200 mb-4" />
              <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No flagged users</h3>
              <p className="font-body text-[#78716C]">No suspicious behavior detected</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="flags-list">
              {flags.map(f => (
                <div key={f.flag_id} className="bg-white rounded-2xl p-6 border border-amber-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`flag-card-${f.flag_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={16} className={f.type === 'duplicate_abuse' ? 'text-red-500' : 'text-amber-500'} />
                        <h3 className="font-body font-semibold text-[#2C2A29]">{f.user_name}</h3>
                        {f.type === 'duplicate_abuse' && (
                          <span className="text-[10px] font-body font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">DUPLICATE x{f.duplicate_count}</span>
                        )}
                      </div>
                      {f.type === 'duplicate_abuse' ? (
                        <div className="space-y-1">
                          <p className="text-sm font-body text-[#78716C]">{f.reason}</p>
                          <p className="text-xs font-body text-[#A89F91]">Source: {f.source_product}</p>
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {(f.reasons || []).map((reason, i) => (
                            <li key={i} className="text-sm font-body text-[#78716C] flex items-start gap-1.5">
                              <span className="text-amber-400 mt-0.5">-</span> {reason}
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="text-xs font-body text-[#A89F91] mt-2">{formatTime(f.latest_at || f.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                      <button onClick={() => navigate(`/profile/${f.user_id}`)}
                        className="border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-cream-50 flex items-center gap-1.5"
                        data-testid={`view-user-${f.flag_id}`}>
                        <Eye size={14} /> Profile
                      </button>
                      <button onClick={() => handleFlagAction(f, 'dismiss')}
                        disabled={processing === f.flag_id}
                        className="border border-cream-100 text-[#78716C] rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-cream-50 disabled:opacity-50"
                        data-testid={`dismiss-flag-${f.flag_id}`}>
                        Dismiss
                      </button>
                      <button onClick={() => handleFlagAction(f, 'block')}
                        disabled={processing === f.flag_id}
                        className="bg-red-500 text-white rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`block-user-${f.flag_id}`}>
                        <ShieldX size={14} /> Block User
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F91]" />
                <input type="text" placeholder="Search by name or email..." value={usersSearch}
                  onChange={e => { setUsersSearch(e.target.value); setUsersPage(1); }}
                  className="w-full bg-white border border-cream-100 rounded-xl pl-10 pr-4 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200"
                  data-testid="users-search-input" />
              </div>
              <span className="text-sm font-body text-[#78716C]">{usersTotal} user{usersTotal !== 1 ? 's' : ''}</span>
            </div>
            {adminUsers.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-users">
                <Users size={48} className="mx-auto text-cream-200 mb-4" />
                <h3 className="font-heading text-xl text-[#2C2A29] mb-2">No users found</h3>
              </div>
            ) : (
              <>
                <div className="space-y-3" data-testid="users-list">
                  {adminUsers.map(u => (
                    <div key={u.user_id} className={`bg-white rounded-2xl p-4 border ${u.status === 'deactivated' ? 'border-amber-200 bg-amber-50/20' : u.is_blocked ? 'border-red-200 bg-red-50/30' : 'border-cream-100/50'} shadow-[0_2px_10px_rgb(0,0,0,0.02)]`}
                      data-testid={`user-card-${u.user_id}`}>
                      <div className="flex items-center gap-4">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className={`w-10 h-10 rounded-full object-cover border-2 border-cream-100 ${u.status === 'deactivated' ? 'opacity-50' : ''}`} />
                        ) : (
                          <div className={`w-10 h-10 rounded-full bg-blush-200 flex items-center justify-center text-sm font-bold text-[#2C2A29] ${u.status === 'deactivated' ? 'opacity-50' : ''}`}>{u.name?.[0]}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-body font-semibold text-[#2C2A29] text-sm truncate">{u.name}</p>
                            {u.is_admin && <span className="text-[10px] font-body font-bold text-white bg-[#2C2A29] px-2 py-0.5 rounded-full">ADMIN</span>}
                            {u.status === 'deactivated' && <span className="text-[10px] font-body font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-0.5"><Ban size={8} />DEACTIVATED</span>}
                            {u.is_blocked && <span className="text-[10px] font-body font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">BLOCKED</span>}
                            {u.is_demo && <span className="text-[10px] font-body font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">DEMO</span>}
                            {u.is_influencer && u.influencer_verified && <span className="text-[10px] font-body font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-0.5"><ShieldCheck size={8} />VERIFIED</span>}
                          </div>
                          <p className="text-xs font-body text-[#78716C] truncate">{u.email}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs font-body text-[#A89F91]">
                            <span>{u.auth_type === 'google' ? 'Google' : 'Email'}</span>
                            <span>{u.listings_count} listing{u.listings_count !== 1 ? 's' : ''}</span>
                            <span>{u.orders_count} order{u.orders_count !== 1 ? 's' : ''}</span>
                            {u.created_at && <span>{new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                          <button onClick={() => navigate(`/profile/${u.user_id}`)}
                            className="border border-cream-100 text-[#78716C] rounded-full p-2 hover:bg-cream-50 transition-all"
                            data-testid={`view-user-profile-${u.user_id}`} title="View profile">
                            <Eye size={14} />
                          </button>
                          <button onClick={() => openBankDialog(u)}
                            className="border border-cream-100 text-[#78716C] rounded-full p-2 hover:bg-emerald-50 hover:text-emerald-700 transition-all"
                            data-testid={`view-user-bank-${u.user_id}`} title="View payout bank details">
                            <Banknote size={14} />
                          </button>
                          {!u.is_admin && (
                            u.status === 'deactivated' ? (
                              <button onClick={() => handleReactivateUser(u.user_id)}
                                disabled={processing === u.user_id}
                                className="border border-green-300 text-green-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-green-50 transition-all disabled:opacity-50 flex items-center gap-1"
                                data-testid={`reactivate-user-${u.user_id}`}>
                                <RotateCw size={12} /> Reactivate
                              </button>
                            ) : (
                              <button onClick={() => handleDeactivateUser(u.user_id)}
                                disabled={processing === u.user_id}
                                className="border border-amber-300 text-amber-600 rounded-full px-3 py-1.5 font-body text-xs font-semibold hover:bg-amber-50 transition-all disabled:opacity-50 flex items-center gap-1"
                                data-testid={`deactivate-user-${u.user_id}`}>
                                <Ban size={12} /> Deactivate
                              </button>
                            )
                          )}
                          {!u.is_admin && (
                            <button onClick={() => setDeleteUserDialog({ open: true, userId: u.user_id, name: u.name, email: u.email })}
                              className="border border-red-200 text-red-400 rounded-full p-2 hover:bg-red-50 hover:text-red-600 transition-all"
                              data-testid={`delete-user-btn-${u.user_id}`} title="Permanently delete">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {usersTotal > 20 && (
                  <div className="flex items-center justify-center gap-4 mt-6">
                    <button onClick={() => setUsersPage(p => Math.max(1, p - 1))} disabled={usersPage <= 1}
                      className="rounded-full p-2 border border-cream-100 text-[#78716C] hover:bg-cream-50 disabled:opacity-30 transition-all"
                      data-testid="users-prev-page"><ChevronLeft size={16} /></button>
                    <span className="text-sm font-body text-[#78716C]">Page {usersPage} of {Math.ceil(usersTotal / 20)}</span>
                    <button onClick={() => setUsersPage(p => p + 1)} disabled={usersPage >= Math.ceil(usersTotal / 20)}
                      className="rounded-full p-2 border border-cream-100 text-[#78716C] hover:bg-cream-50 disabled:opacity-30 transition-all"
                      data-testid="users-next-page"><ChevronRight size={16} /></button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(v) => setRejectDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Reject Verification</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Select a reason for rejecting {rejectDialog.name}'s influencer request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { value: 'content_not_relevant', label: 'Content not beauty-related' },
              { value: 'wrong_expired_code', label: 'Wrong or expired code' },
              { value: 'account_private', label: 'Account is private' },
              { value: 'other', label: 'Other reason' },
            ].map(opt => (
              <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${rejectReason === opt.value ? 'border-red-300 bg-red-50' : 'border-cream-100 hover:border-red-200'}`}>
                <input type="radio" name="reject-reason" value={opt.value} checked={rejectReason === opt.value} onChange={() => setRejectReason(opt.value)} className="accent-red-500" data-testid={`reject-reason-${opt.value}`} />
                <span className="text-sm font-body text-[#2C2A29]">{opt.label}</span>
              </label>
            ))}
            {rejectReason === 'other' && (
              <input
                type="text"
                value={rejectOtherText}
                onChange={e => setRejectOtherText(e.target.value)}
                maxLength={50}
                placeholder="Short reason (6-7 words)"
                className="w-full border border-cream-100 rounded-xl px-3 py-2 text-sm font-body text-[#2C2A29] focus:outline-none focus:border-red-300 placeholder:text-[#A89F91]"
                data-testid="reject-other-text"
              />
            )}
            <div className="flex gap-3 pt-1">
              <button onClick={() => { setRejectDialog({ open: false, userId: null, name: '' }); setRejectReason(''); setRejectOtherText(''); }}
                className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Cancel</button>
              <button onClick={handleReject} disabled={processing || !rejectReason || (rejectReason === 'other' && !rejectOtherText.trim())}
                className="flex-1 bg-red-500 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                <ShieldX size={14} /> Reject
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(v) => setCancelDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Cancel Order</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Cancel order for "{cancelDialog.productName}"? Stock will be restored to the listing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <button onClick={() => setCancelDialog({ open: false, orderId: null, productName: '' })}
              className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="cancel-order-dialog-cancel">Keep Order</button>
            <button onClick={() => handleCancelOrder(cancelDialog.orderId)}
              disabled={processing === cancelDialog.orderId}
              className="flex-1 bg-red-500 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="cancel-order-dialog-confirm">
              <XCircle size={14} /> {processing === cancelDialog.orderId ? 'Cancelling...' : 'Cancel Order'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Listing Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(v) => setDeleteDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="delete-listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-600">Permanently Delete Listing?</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              This will <strong className="text-red-600">permanently remove</strong> "{deleteDialog.productName}" and all associated data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs font-body text-[#A89F91] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-2">
            Tip: Use "Remove" instead if you just want to hide this listing. Removed listings can be restored later.
          </p>
          <div className="flex gap-3 mt-3">
            <button onClick={() => setDeleteDialog({ open: false, listingId: null, productName: '' })}
              className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="delete-listing-dialog-cancel">Cancel</button>
            <button onClick={() => handleDeleteListing(deleteDialog.listingId)}
              disabled={processing === deleteDialog.listingId}
              className="flex-1 bg-red-600 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="delete-listing-dialog-confirm">
              <Trash2 size={14} /> {processing === deleteDialog.listingId ? 'Deleting...' : 'Delete Forever'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Video Confirmation Dialog — parallels the listing one but
          kept separate so the copy is precise to videos (description mentions
          "video" + the title rather than the listing brand+product). */}
      <Dialog open={videoDeleteDialog.open} onOpenChange={(v) => setVideoDeleteDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="delete-video-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-600">Permanently Delete Video?</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              This will <strong className="text-red-600">permanently remove</strong> "{videoDeleteDialog.title}" and all its likes and pending reports. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs font-body text-[#A89F91] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-2">
            Tip: Use "Take down" instead if you just want to hide this video. Removed videos can be restored later.
          </p>
          <div className="flex gap-3 mt-3">
            <button onClick={() => setVideoDeleteDialog({ open: false, videoId: null, title: '' })}
              className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="delete-video-dialog-cancel">Cancel</button>
            <button onClick={() => handleDeleteVideo(videoDeleteDialog.videoId)}
              disabled={processing === videoDeleteDialog.videoId}
              className="flex-1 bg-red-600 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="delete-video-dialog-confirm">
              <Trash2 size={14} /> {processing === videoDeleteDialog.videoId ? 'Deleting...' : 'Delete Forever'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirmation Dialog */}
      {/* Komerce Debug Dialog — shows the exact 3 curls to share with Komerce
          support when a shipment is stuck (AWB pending overnight, etc). The
          API key is revealed here for admin convenience; the "Copy" buttons
          copy each curl verbatim. */}
      <Dialog open={komerceDebug.open} onOpenChange={(v) => setKomerceDebug(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-2xl mx-4 max-h-[85vh] overflow-y-auto" data-testid="komerce-debug-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Komerce Debug · {komerceDebug.orderId}</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Share the relevant curl below with Komerce support so they can trace the issue on their side. <span className="text-amber-700 font-medium">⚠️ These curls contain your live API key — share only via a private/secure channel.</span>
            </DialogDescription>
          </DialogHeader>

          {komerceDebug.data && (
            <div className="space-y-4 mt-2">
              {/* Current order state */}
              <div className="bg-cream-50 rounded-xl p-3 text-xs font-body">
                <p className="font-semibold text-[#2C2A29] mb-1">Current state on our side</p>
                <div className="grid grid-cols-2 gap-1 font-mono text-[11px]">
                  <span className="text-[#78716C]">Komerce order_no:</span><span className="text-[#2C2A29]">{komerceDebug.data.ro_order_no || '—'}</span>
                  <span className="text-[#78716C]">Payment status:</span><span className="text-[#2C2A29]">{komerceDebug.data.current_state?.payment_status || '—'}</span>
                  <span className="text-[#78716C]">Pickup status:</span><span className="text-[#2C2A29]">{komerceDebug.data.current_state?.ro_pickup_status || '—'}</span>
                  <span className="text-[#78716C]">AWB / Tracking:</span><span className="text-[#2C2A29]">{komerceDebug.data.current_state?.tracking_number || '— (pending)'}</span>
                  <span className="text-[#78716C]">Last webhook:</span><span className="text-[#2C2A29]">{komerceDebug.data.current_state?.ro_last_webhook_status || '— (never)'}</span>
                  <span className="text-[#78716C]">Last webhook at:</span><span className="text-[#2C2A29]">{komerceDebug.data.current_state?.ro_last_webhook_at?.slice(0, 16).replace('T', ' ') || '— (never)'}</span>
                </div>
              </div>

              {/* Each curl block */}
              {[
                { label: 'A. Original booking request (the curl we sent at booking)', value: komerceDebug.data.curl_store_order, hint: 'This is what Komerce support typically asks for first.' },
                { label: 'B. Get current Komerce-side state for this order', value: komerceDebug.data.curl_detail_order, hint: 'Lets support compare their record vs ours.' },
                { label: 'C. Get carrier-side AWB history', value: komerceDebug.data.curl_history_airway_bill, hint: 'Only available once an AWB exists.' },
              ].map((blk, i) => blk.value && (
                <div key={i} className="border border-cream-100 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="font-semibold text-sm text-[#2C2A29]">{blk.label}</p>
                    <button onClick={() => { navigator.clipboard.writeText(blk.value); toast.success('Copied to clipboard'); }}
                      className="bg-cream-50 hover:bg-cream-100 text-[#2C2A29] rounded-full px-3 py-1 text-xs font-body font-semibold flex items-center gap-1 transition-all"
                      data-testid={`copy-curl-${i}`}>
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                  <p className="text-xs font-body text-[#78716C] mb-2">{blk.hint}</p>
                  <pre className="bg-stone-900 text-stone-100 rounded-lg p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{blk.value}</pre>
                </div>
              ))}

              <p className="text-xs font-body text-[#78716C] italic">
                Tip: usually you only need to share <strong>(A)</strong> with the support team and ask them to investigate why the AWB hasn't been issued.
              </p>
            </div>
          )}

          <div className="flex justify-end mt-2">
            <button onClick={() => setKomerceDebug({ open: false, orderId: null, data: null })}
              className="border border-cream-100 text-[#2C2A29] rounded-full px-5 py-2 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="komerce-debug-close">Close</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOrderDialog.open} onOpenChange={(v) => setDeleteOrderDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="delete-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Delete Transaction</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Permanently delete order #{deleteOrderDialog.orderId_display}? If the order is active, stock will be restored.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <button onClick={() => setDeleteOrderDialog({ open: false, orderId: null, orderId_display: '' })}
              className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="delete-order-dialog-cancel">Keep</button>
            <button onClick={() => handleDeleteOrder(deleteOrderDialog.orderId)}
              disabled={processing === deleteOrderDialog.orderId}
              className="flex-1 bg-red-500 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="delete-order-dialog-confirm">
              <Trash2 size={14} /> {processing === deleteOrderDialog.orderId ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Paid (Manual Reconcile) Dialog — for stuck pending orders where buyer paid but gateway didn't flip */}
      <Dialog open={markPaidDialog.open} onOpenChange={(v) => setMarkPaidDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-md mx-4" data-testid="mark-paid-manual-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Mark Paid (Manual Reconcile)</DialogTitle>
            <DialogDescription className="font-body text-[#78716C] space-y-2">
              <span className="block">
                Order <span className="font-mono">#{markPaidDialog.orderId}</span> — amount {formatPrice(markPaidDialog.amount)}.
              </span>
              <span className="block text-xs">
                Use this when you've verified the payment <strong>outside</strong> of the gateway (e.g., on GoPay Merchant, bank statement). This will move the order to <strong>Paid</strong> and let the seller ship.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="text-xs font-body text-[#78716C] block mb-1">Reconciliation note (required)</label>
            <textarea
              value={markPaidDialog.note}
              onChange={(e) => setMarkPaidDialog(d => ({ ...d, note: e.target.value }))}
              rows={3}
              placeholder="e.g., Verified on GoPay Merchant dashboard — txn ID 1234567890, paid 16:25 WIB"
              className="w-full border border-cream-100 rounded-xl px-3 py-2 text-sm font-body resize-none focus:outline-none focus:ring-2 focus:ring-[#F4C2C2]/40"
              data-testid="mark-paid-manual-note"
            />
          </div>
          <div className="flex gap-3 mt-3">
            <button onClick={() => setMarkPaidDialog({ open: false, orderId: null, amount: 0, note: '' })}
              className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="mark-paid-manual-cancel">Cancel</button>
            <button onClick={handleMarkPaidManual}
              disabled={processing === markPaidDialog.orderId || !markPaidDialog.note.trim()}
              className="flex-1 bg-emerald-600 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="mark-paid-manual-confirm">
              <CheckCircle2 size={14} /> {processing === markPaidDialog.orderId ? 'Reconciling…' : 'Mark Paid'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserDialog.open} onOpenChange={(v) => setDeleteUserDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-600">Permanently Delete User?</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              This will <strong className="text-red-600">permanently remove</strong> <strong>{deleteUserDialog.name}</strong> ({deleteUserDialog.email}), including their account, profile, listings, and sessions. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs font-body text-[#A89F91] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-2">
            Tip: Use "Deactivate" instead to suspend the user while preserving their data. Deactivated users can be reactivated later.
          </p>
          <div className="flex gap-3 mt-3">
            <button onClick={() => setDeleteUserDialog({ open: false, userId: null, name: '', email: '' })}
              className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
              data-testid="delete-user-dialog-cancel">Cancel</button>
            <button onClick={() => handleDeleteUser(deleteUserDialog.userId)}
              disabled={processing === deleteUserDialog.userId}
              className="flex-1 bg-red-600 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="delete-user-dialog-confirm">
              <Trash2 size={14} /> {processing === deleteUserDialog.userId ? 'Deleting...' : 'Delete Forever'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: view a user's saved payout bank details */}
      <Dialog open={bankDialog.open} onOpenChange={(v) => setBankDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-md mx-4" data-testid="admin-bank-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29] flex items-center gap-2">
              <Banknote size={20} className="text-emerald-700" /> Payout bank — {bankDialog.user?.name}
            </DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">{bankDialog.user?.email}</DialogDescription>
          </DialogHeader>
          {bankDialog.loading ? (
            <div className="flex items-center gap-2 text-sm font-body text-[#78716C] py-6 justify-center">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : bankDialog.bank ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 my-2" data-testid="admin-bank-dialog-saved">
              <p className="text-[11px] uppercase tracking-wide font-semibold text-emerald-800 mb-1.5">Saved payout details</p>
              <p className="text-base font-body font-semibold text-emerald-900 leading-snug">
                {bankDialog.bank.bank_name === 'Other' && bankDialog.bank.bank_name_other
                  ? bankDialog.bank.bank_name_other
                  : bankDialog.bank.bank_name}
              </p>
              <p className="text-sm font-body text-emerald-900 leading-snug">A.n. {bankDialog.bank.account_holder}</p>
              <p className="text-sm font-body text-emerald-900 leading-snug font-mono tabular-nums">{bankDialog.bank.account_number}</p>
              {bankDialog.bank.updated_at && (
                <p className="text-[11px] font-body text-emerald-700 italic mt-2">Last updated {new Date(bankDialog.bank.updated_at).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              )}
              <button
                onClick={async () => {
                  const txt = `${bankDialog.bank.bank_name === 'Other' ? bankDialog.bank.bank_name_other : bankDialog.bank.bank_name}\n${bankDialog.bank.account_holder}\n${bankDialog.bank.account_number}`;
                  try { await navigator.clipboard.writeText(txt); toast.success('Bank details copied'); }
                  catch { toast.error('Copy failed'); }
                }}
                className="mt-3 inline-flex items-center gap-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-full px-3 py-1.5 font-body text-xs font-semibold"
                data-testid="admin-bank-dialog-copy">
                <Copy size={11} /> Copy
              </button>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 my-2 text-sm font-body text-amber-900 flex items-start gap-2" data-testid="admin-bank-dialog-empty">
              <AlertTriangle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
              <span>This user hasn't saved any payout bank details yet.</span>
            </div>
          )}
          <div className="flex justify-end mt-2">
            <button onClick={() => setBankDialog(d => ({ ...d, open: false }))}
              className="px-4 py-2 rounded-full bg-cream-100 text-[#2C2A29] font-body text-sm font-semibold hover:bg-cream-200"
              data-testid="admin-bank-dialog-close">Close</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
